/**
 * AiChat — world-class chat UI using assistant-ui primitives.
 *
 * Shared between AiChatPanel (slide-over) and AiChatPage (dedicated page).
 * Full-featured: markdown rendering, tool progress, message editing,
 * branch navigation, file attachments, and thinking indicators.
 */
import React, { type FC, useState, useRef, useEffect, useCallback, useContext, useMemo, createContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  BranchPickerPrimitive,
  ActionBarPrimitive,
  AttachmentPrimitive,
  useAui,
  useAuiEvent,
  useAuiState,
  useMessage,
  useThreadComposerAttachment,
  useThreadViewportStore,
} from '@assistant-ui/react';
import {
  ArrowUp,
  Paperclip,
  Square,
  Pencil,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Copy,
  RotateCcw,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Database,
  TrendingUp,
  Zap,
  Search,
  FileText,
  Globe,
  Image as ImageIcon,
  FileSpreadsheet,
  File,
  Terminal,
  Code2,
  Download,
  FolderOpen,
  DollarSign,
  ChartPie,
  Wrench,
  Plus,
  GitBranch,
  Gauge,
  Layers,
  Brain,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import { ToolRow, DetailPanel, DetailPre, plainText, Shimmer } from './toolRow';
import { useModelSelection, AI_MODELS, EFFORT_LABELS, type AiModelId, type EffortLevel, useThreadState } from './AiChatProvider';
import { ScriptToolUI } from './tool-renderers/ScriptToolUI';
import { useShellOutputBroadcaster, useSummaryProgress, useCostSignal, useContextSignal, useMacUsageSignal, useSandboxStatus, useWorkspaceRestoreStatus } from './useShellOutputStream';
import { DataTableToolUI } from './tool-renderers/DataTableToolUI';
import { ImageGenToolUI } from './tool-renderers/ImageGenToolUI';
import { TodosToolUI } from './tool-renderers/TodosToolUI';
import { AskUserToolUI } from './tool-renderers/AskUserToolUI';
import { BatchScreenshotToolUI } from './tool-renderers/BatchScreenshotToolUI';
import { BatchRenderVideoToolUI } from './tool-renderers/BatchRenderVideoToolUI';
import { VcRenderStatusToolUI, VcGetPreviewToolUI, VfxGetJobToolUI } from './tool-renderers/RenderJobToolUI';
import { HtmlArtifactCard, extractHtmlTargets } from './tool-renderers/HtmlArtifactCard';
import { api } from '@/services/api';
import { toast } from '@/components/ui/Toast';
import { threadApi } from './adapters/firestore-thread-adapter';
import { reportSurgeryFailure } from './threadSurgery';
import { BlobRefView, extractBlobRef } from './BlobRefView';
import { useLegend, rehypeTerms, makeTermSpan } from './TermLegend';
import { SummarySheet, useSummaryView } from './SummarySheet';
import { useTurnTiming, turnTimestamp } from './turnTiming';
import {
  deriveMessageFold,
  formatDuration,
  formatSummary,
  groupExploration,
  type FoldPart,
  type FoldToolPart,
  type MessageFold,
} from './turnFold';
import { useFoldOpen, collapseAllFolds } from './foldState';
import { BackgroundTasksBar, TaskNotificationNotice, isTaskNotificationText } from './BackgroundTasks';

function safeStringify(value: unknown, indent = 2): string {
  try {
    return JSON.stringify(value, null, indent);
  } catch {
    return String(value);
  }
}

/**
 * Opens a thread at its latest message.  assistant-ui does this on its own
 * `thread.initialize` / `threadListItem.switchedTo` events, but our threads load
 * through useChat, which fires neither, and autoscroll alone only follows if
 * the view starts at the bottom.  So: once per thread,
 * when its messages first appear, jump to the bottom.  The store's scrollToBottom
 * keeps following while late content (images, code blocks) settles.
 */
function ScrollToLatestOnOpen() {
  const { currentThreadId } = useThreadState();
  const messageCount = useAuiState((s: any) => s.thread?.messages?.length ?? 0) as number;
  const viewportStore = useThreadViewportStore();
  const openedFor = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (openedFor.current === currentThreadId || messageCount === 0) return;
    openedFor.current = currentThreadId;
    requestAnimationFrame(() => viewportStore.getState().scrollToBottom({ behavior: 'instant' }));
  }, [currentThreadId, messageCount, viewportStore]);

  return null;
}

interface AiChatProps {
  className?: string;
}

export const AiChat: FC<AiChatProps> = ({ className = '' }) => {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Only hold the workspace SSE channel open while a run/tool is active so idle
  // threads don't pin a Cloud Run instance alive (request-based billing).
  const threadRunning = useAuiState((s: any) => s.thread?.isRunning ?? false);
  useShellOutputBroadcaster(threadRunning);
  // A new run closes the folds the user had opened in this thread.
  const { currentThreadId } = useThreadState();
  useAuiEvent('thread.runStart', () => collapseAllFolds(currentThreadId ?? ''));

  return (
    <LightboxContext.Provider value={setLightboxUrl}>
      <ThreadPrimitive.Root className={`ai-chat flex flex-col h-full bg-white ${className}`}>
        <ScriptToolUI />
        <DataTableToolUI />
        <ImageGenToolUI />
        <TodosToolUI />
        <AskUserToolUI />
        <BatchScreenshotToolUI />
        <BatchRenderVideoToolUI />
        <VcRenderStatusToolUI />
        <VcGetPreviewToolUI />
        <VfxGetJobToolUI />

        <DropZoneOverlay>
          {/* Bottom-anchored: sending does not move the view; once the reply reaches
              the bottom edge the view follows it, and scrolling up stops the follow.
              No `scroll-smooth`: it turns each follow step into a lagging animation. */}
          <ThreadPrimitive.Viewport turnAnchor="bottom" className="flex-1 overflow-y-auto overflow-x-hidden">
            <ScrollToLatestOnOpen />
            <ThreadPrimitive.Empty>
              <WelcomeScreen />
            </ThreadPrimitive.Empty>

            <ThreadPrimitive.Messages
              components={{
                UserMessage: UserMessageComponent,
                AssistantMessage: AssistantMessageComponent,
                UserEditComposer: UserEditComposerComponent,
              }}
            />
          </ThreadPrimitive.Viewport>
        </DropZoneOverlay>

        <ChatComposer />
      </ThreadPrimitive.Root>

      {lightboxUrl && (
        <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}
    </LightboxContext.Provider>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Lightbox Context
// ═══════════════════════════════════════════════════════════════════════════════

export const LightboxContext = createContext<(url: string) => void>(() => {});

// ═══════════════════════════════════════════════════════════════════════════════
// Welcome Screen
// ═══════════════════════════════════════════════════════════════════════════════

function WelcomeScreen() {
  // No hero: one quiet line and a few plain-text starters above the composer.
  const suggestions = [
    'How many installs did we get last week?',
    'Show marketing performance by campaign',
    'Compare user retention week over week',
  ];

  return (
    <div className="mx-auto flex h-full min-h-[320px] w-full max-w-3xl flex-col justify-end px-4 pb-6 sm:px-6">
      <p className="text-meta text-zinc-500">Ask about analytics, campaigns, revenue, or run a query or script.</p>
      <div className="mt-2 flex flex-col items-start gap-0.5">
        {suggestions.map((prompt) => (
          <ThreadPrimitive.Suggestion
            key={prompt}
            prompt={prompt}
            autoSend
            className="text-meta text-zinc-400 transition-colors hover:text-zinc-900 cursor-pointer text-left"
          >
            {prompt}
          </ThreadPrimitive.Suggestion>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// User Message
// ═══════════════════════════════════════════════════════════════════════════════

function UserMessageComponent() {
  // A wake turn's message is the notification of background tasks ending
  // (agent-core ai-chat.tasks): a system notice, not something the user typed.
  const parts = useAuiState((s) => s.message.parts) as readonly any[];
  const notice = parts?.length === 1 && parts[0]?.type === 'text' && isTaskNotificationText(parts[0].text)
    ? parts[0].text as string
    : null;
  if (notice) {
    return (
      <MessagePrimitive.Root>
        <TaskNotificationNotice text={notice} />
      </MessagePrimitive.Root>
    );
  }
  return (
    <MessagePrimitive.Root className="group mx-auto flex w-full max-w-3xl flex-col items-end px-4 pt-6 sm:px-6">
      <div className="max-w-[85%] min-w-0 rounded-lg bg-accent-50 px-3.5 py-2">
        <MessagePrimitive.Content
          components={{
            Text: UserTextPart,
            File: UserFilePart,
          }}
        />
      </div>

      <div className="mt-0.5 -mb-3 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <ActionBarPrimitive.Edit className={MSG_ACTION} title="Edit">
          <Pencil className="w-3.5 h-3.5" />
        </ActionBarPrimitive.Edit>
        <RevertForkActions />
      </div>
    </MessagePrimitive.Root>
  );
}

/** Hover actions under a message: small, borderless, grey. */
const MSG_ACTION =
  'w-7 h-7 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors disabled:opacity-40';

const LEGACY_FILE_REF_PATTERN = /\[file:\s*[^\]]+\]\(https?:\/\/[^)]+\)\s*\([^)]*\)/g;
const WS_REF_PATTERN = /\[workspace:\s*([^\]]+)\]/g;

function UserTextPart({ text }: { text: string; status: any }) {
  const stripped = text.replace(LEGACY_FILE_REF_PATTERN, '');
  const wsRefs: string[] = [];
  let m;
  WS_REF_PATTERN.lastIndex = 0;
  while ((m = WS_REF_PATTERN.exec(stripped)) !== null) {
    wsRefs.push(m[1].trim());
  }
  const cleaned = stripped.replace(WS_REF_PATTERN, '').trim();

  return (
    <>
      {wsRefs.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {wsRefs.map((path, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white border border-zinc-200 text-xs text-zinc-700">
              <FolderOpen className="w-3 h-3 text-zinc-400 shrink-0" />
              <span className="truncate max-w-[160px]">{path.split('/').pop() ?? path}</span>
            </span>
          ))}
        </div>
      )}
      {cleaned && <p className="text-chat text-zinc-900 whitespace-pre-wrap break-words">{cleaned}</p>}
    </>
  );
}

function UserFilePart({ data, mimeType, filename }: { data: string; mimeType: string; filename?: string; status: any }) {
  const openLightbox = useContext(LightboxContext);
  const isImage = mimeType?.startsWith('image/');
  const displayName = filename ?? (() => {
    try { return decodeURIComponent(new URL(data).pathname.split('/').pop() ?? 'file'); }
    catch { return 'file'; }
  })();

  if (isImage) {
    return (
      <button
        onClick={() => openLightbox(data)}
        className="block rounded-md overflow-hidden border border-zinc-200 hover:border-zinc-300 transition-colors cursor-zoom-in text-left my-1"
      >
        <img src={data} alt={displayName} className="max-w-[200px] max-h-[140px] object-cover" />
      </button>
    );
  }

  return (
    <a
      href={data}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-2.5 py-1.5 my-1 rounded-md bg-white border border-zinc-200 hover:bg-zinc-50 transition-colors"
    >
      <FileTypeIcon contentType={mimeType} className="w-4 h-4 text-zinc-400 shrink-0" />
      <span className="text-xs text-zinc-600 truncate max-w-[200px]">{displayName}</span>
    </a>
  );
}


function FileTypeIcon({ contentType, className }: { contentType?: string; className?: string }) {
  if (contentType?.startsWith('image/')) return <ImageIcon className={className} />;
  if (contentType === 'application/pdf') return <FileText className={className} />;
  if (contentType?.includes('spreadsheet') || contentType?.includes('excel') || contentType === 'text/csv')
    return <FileSpreadsheet className={className} />;
  if (contentType === 'application/json' || contentType === 'text/plain' || contentType === 'text/markdown')
    return <FileText className={className} />;
  return <File className={className} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Branching a stored conversation
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Cut the STORED transcript at a message, before a branching action re-runs it.
 *
 * Editing a message and reloading an assistant one both rewrite only the
 * client's copy: the runtime slices its own list and streams a fresh turn from
 * there.  What gets persisted afterwards is therefore SHORTER than what
 * Firestore holds, and the destructive-shrink guard refuses it — from the
 * server's side an incoming list that's missing the tail is indistinguishable
 * from a half-loaded client about to truncate a thread it never fully read.  So
 * the new turn streamed, looked saved, and was gone on refresh, with the
 * pre-edit turn back in its place.
 *
 * Declaring the branch server-side first is what makes the two agree: this is
 * the same call the Revert menu makes, dropping the message and everything after
 * it, and carrying the summaries, telemetry, todos and checkpoints across the
 * cut.  The workspace is deliberately left alone — re-running the conversation
 * from an earlier point is not a request to undo the files, and the Revert menu
 * is where rolling those back is offered explicitly.
 *
 * Returns false when the cut failed, and then the caller must NOT proceed:
 * running the turn regardless is precisely how the two copies drifted apart.
 */
function useStoredBranchCut(): (
  messageId: string | undefined,
  opts?: { restoreWorkspace?: boolean },
) => Promise<boolean> {
  const { currentThreadId, refreshThreads } = useThreadState();
  return useCallback(
    async (messageId: string | undefined, opts?: { restoreWorkspace?: boolean }) => {
      // No saved thread, or a message that was never persisted (ephemeral
      // panel): there is no stored tail to cut, so the local branch is the
      // whole truth already.
      if (!currentThreadId || !messageId) return true;
      const restoreWorkspace = !!opts?.restoreWorkspace;
      try {
        const res = await threadApi.revert(currentThreadId, { messageId, restoreWorkspace });
        refreshThreads();
        // Asked for the files back and didn't get them (no snapshot at this
        // message, or one belonging to a branch an earlier revert discarded).
        // Say so, or the turn runs on the current files as if the request had
        // been honoured.
        if (restoreWorkspace && !res.restoredWorkspace && !res.workspaceRestorePending) {
          toast.info('No usable workspace snapshot at this message — the files were left as they are.');
        }
        return true;
      } catch (err) {
        reportSurgeryFailure(err, {
          threadId: currentThreadId,
          fallback: "Couldn't rewrite the conversation from this message — nothing was sent.",
        });
        return false;
      }
    },
    [currentThreadId, refreshThreads],
  );
}

/**
 * The two scopes a rewrite can have, in one dialog.
 *
 * Reverting to a message and editing one are the same cut with the same open
 * question — do the files go back too? — so both raise this dialog instead of each
 * one hard-coding an answer.  A centered dialog, not a dropdown off the button:
 * where a dropdown lands depends on where the message happens to sit on screen.
 */
function RevertScopeDialog({
  title,
  busy,
  onChoose,
  onClose,
}: {
  title: string;
  busy: null | 'chat' | 'workspace';
  onChoose: (restoreWorkspace: boolean) => void;
  onClose: () => void;
}) {
  // Esc closes, except mid-revert: the cut is already on its way.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const choice =
    'w-full flex items-start gap-3 rounded-md border border-zinc-200 px-3 py-2.5 text-left transition-colors hover:border-accent-200 hover:bg-accent-50 disabled:opacity-50 disabled:hover:bg-white disabled:hover:border-zinc-200';
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/20" onClick={() => !busy && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-sm rounded-lg bg-white shadow-lg border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-4 pb-3">
          <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
          <p className="mt-1 text-xs leading-snug text-zinc-500">
            Everything after this message is dropped. Tool side-effects (campaigns, shell, external APIs) can't be undone.
          </p>
        </div>
        <div className="space-y-2 px-5">
          <button type="button" autoFocus disabled={!!busy} onClick={() => onChoose(false)} className={choice}>
            {busy === 'chat'
              ? <Loader2 className="mt-0.5 w-4 h-4 animate-spin shrink-0 text-zinc-500" />
              : <RotateCcw className="mt-0.5 w-4 h-4 shrink-0 text-zinc-500" />}
            <span>
              <span className="block text-meta font-medium text-zinc-900">Chat only</span>
              <span className="block text-xs text-zinc-500">Workspace files stay as they are now.</span>
            </span>
          </button>
          <button type="button" disabled={!!busy} onClick={() => onChoose(true)} className={choice}>
            {busy === 'workspace'
              ? <Loader2 className="mt-0.5 w-4 h-4 animate-spin shrink-0 text-zinc-500" />
              : <FolderOpen className="mt-0.5 w-4 h-4 shrink-0 text-zinc-500" />}
            <span>
              <span className="block text-meta font-medium text-zinc-900">Chat + workspace files</span>
              <span className="block text-xs text-zinc-500">
                Files go back to this message's snapshot; anything created after it is deleted. Without a snapshot at
                this exact message, only the chat reverts.
              </span>
            </span>
          </button>
        </div>
        <div className="flex justify-end px-5 py-3">
          <button
            type="button"
            disabled={!!busy}
            onClick={onClose}
            className="px-2.5 py-1.5 rounded-md text-xs font-medium text-zinc-600 hover:bg-zinc-100 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// User Edit Composer
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * "Save & Send" — `ComposerPrimitive.Send` plus the stored-transcript cut.
 *
 * Sending an edit reverts the thread to this message, so it asks the same
 * question the Revert menu does rather than quietly choosing chat-only: the
 * discarded turns may have written files, and whether those should go back with
 * the conversation is the user's call, not a default.
 *
 * The disabled rule mirrors the primitive's own (`thread.isRunning ||
 * !composer.isEditing || composer.isEmpty`), since replacing the button means
 * replacing that too.
 */
function EditSendButton() {
  const aui = useAui();
  const messageId = useMessage().id;
  const cutStoredBranch = useStoredBranchCut();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<null | 'chat' | 'workspace'>(null);
  const blocked = useAuiState(
    (s) => s.thread.isRunning || !s.composer.isEditing || s.composer.isEmpty,
  );

  const saveAndSend = async (restoreWorkspace: boolean) => {
    setBusy(restoreWorkspace ? 'workspace' : 'chat');
    if (!(await cutStoredBranch(messageId, { restoreWorkspace }))) {
      setBusy(null);
      setOpen(false);
      return;
    }
    // Sending leaves edit mode, which unmounts this button — so no reset here.
    aui.composer().send();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={blocked || !!busy}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-accent-500 text-white text-xs font-medium hover:bg-accent-600 transition-colors disabled:opacity-40"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        Save &amp; Send
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>

      {open && (
        <RevertScopeDialog
          title="Send edit, rewriting from here"
          busy={busy}
          onChoose={saveAndSend}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Re-run an assistant message, cutting the stored transcript at it first.
 *
 * Same divergence as an edit: the runtime drops this message locally and streams
 * a replacement, so unless the stored copy is cut too, the persist that follows
 * is a shrink and gets refused.  Re-running the LAST message happened to survive
 * — one message out, one message in, so the transcript never got shorter — which
 * is why this only ever bit re-runs from further up the thread.
 *
 * The disabled rule mirrors `ActionBarPrimitive.Reload`'s own.
 */
function useMessageReload(): { reload: () => Promise<void>; blocked: boolean; busy: boolean } {
  const aui = useAui();
  const messageId = useMessage().id;
  const cutStoredBranch = useStoredBranchCut();
  const [busy, setBusy] = useState(false);
  const blocked = useAuiState(
    (s) => s.thread.isRunning || s.thread.isDisabled || s.message.role !== 'assistant',
  );

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      if (await cutStoredBranch(messageId)) aui.message().reload();
    } finally {
      setBusy(false);
    }
  }, [aui, cutStoredBranch, messageId]);

  return { reload, blocked, busy };
}

function ReloadButton() {
  const { reload, blocked, busy } = useMessageReload();
  if (blocked) return null;
  return (
    <button
      type="button"
      onClick={() => void reload()}
      disabled={busy}
      title="Re-run from here"
      className={MSG_ACTION}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
    </button>
  );
}

function UserEditComposerComponent() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 pt-6 pb-2 sm:px-6">
      <div className="ml-auto w-full max-w-[85%] min-w-0">
        <ComposerPrimitive.Root className="flex flex-col gap-2">
          <ComposerPrimitive.Input
            className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2 text-chat text-zinc-900 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5 focus:outline-none min-h-[60px]"
          />
          <div className="flex justify-end gap-2">
            <ComposerPrimitive.Cancel className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-zinc-600 text-xs font-medium hover:bg-zinc-100 transition-colors">
              Cancel
            </ComposerPrimitive.Cancel>
            <EditSendButton />
          </div>
        </ComposerPrimitive.Root>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Assistant Message
// ═══════════════════════════════════════════════════════════════════════════════

function AssistantMessageComponent() {
  return (
      <MessagePrimitive.Root className="group mx-auto w-full max-w-3xl px-4 pt-2 pb-2 sm:px-6">
        <SafeRenderBoundary label="message content">
          <AssistantParts />
        </SafeRenderBoundary>
        <MessagePrimitive.Error>
          <ErrorDisplay />
        </MessagePrimitive.Error>

        <div className="mt-1 -ml-1.5 flex items-center gap-0.5">
          <BranchPicker />
          <div className="flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <ActionBarPrimitive.Copy className={MSG_ACTION} title="Copy">
              <Copy className="w-3.5 h-3.5" />
            </ActionBarPrimitive.Copy>
            <ReloadButton />
            <RetryWithModelButton />
          </div>
        </div>
      </MessagePrimitive.Root>
  );
}

// ── Turn fold, exploration groups, live status ─────────────────────────────
//
// Parts are laid out by hand with `MessagePrimitive.PartByIndex` rather than
// `Unstable_PartsGrouped`: the latter keys each group by its *position*
// (`group-${i}-…`), so when a message settles and its work folds away, the
// answer below the fold would remount.  Here every part keeps the key
// `part-<index>` in one flat list, so parts that stay visible keep their state.

/** One component map for every part, so `PartByIndex`'s memo holds. */
const ASSISTANT_PART_COMPONENTS = {
  Text: AssistantTextPart,
  Reasoning: AssistantReasoningPart,
  tools: { Fallback: ToolCallFallback },
};

function AssistantPart({ index }: { index: number }) {
  return <MessagePrimitive.PartByIndex index={index} components={ASSISTANT_PART_COMPONENTS} />;
}

/** When each message was first seen, for an elapsed time when the turn has no `startedAt`. */
const firstSeenAt = new Map<string, number>();

function AssistantParts() {
  const parts = useAuiState((s) => s.message.parts) as readonly FoldPart[];
  const status = useAuiState((s) => s.message.status);
  const messageId = useAuiState((s) => s.message.id);
  const timing = useTurnTiming();

  const hasError = status?.type === 'incomplete' && status.reason === 'error';
  const aborted = timing?.aborted === true || (status?.type === 'incomplete' && status.reason === 'cancelled');
  // A turn stopped mid-tool-call keeps a call with no result, which assistant-ui
  // reports as `requires-action` (as it does for ask_user); only the latter is live.
  const running = status?.type === 'running' || (status?.type === 'requires-action' && !aborted);

  const fold = useMemo(
    () => deriveMessageFold(parts, {
      running,
      hasError,
      aborted,
      startedAt: timing?.startedAt,
      finishedAt: timing?.finishedAt,
    }),
    [parts, running, hasError, aborted, timing?.startedAt, timing?.finishedAt],
  );

  const { hidden, groups } = useMemo(() => {
    const hidden = new Set(fold?.hiddenIndices ?? []);
    const visible = parts.map((_, i) => i).filter((i) => !hidden.has(i));
    return { hidden, groups: groupExploration(parts, visible) };
  }, [parts, fold]);

  const items: ReactNode[] = [];
  const consumed = new Set<number>();
  for (let i = 0; i < parts.length; i++) {
    if (fold && i === fold.anchorIndex) {
      items.push(
        <TurnFoldRow key="fold" fold={fold}>
          {fold.hiddenIndices.map((h) => <AssistantPart key={`part-${h}`} index={h} />)}
        </TurnFoldRow>,
      );
    }
    if (hidden.has(i) || consumed.has(i)) continue;
    const group = groups.find((g) => g.start === i);
    if (group) {
      group.indices.forEach((g) => consumed.add(g));
      const live = running ? group.indices.map((g) => parts[g]).find((p) => isInFlightTool(p)) as FoldToolPart | undefined : undefined;
      items.push(
        <ExplorationRow key={`explore-${group.start}`} label={group.label} running={!!live}>
          {group.indices.map((g) => <AssistantPart key={`part-${g}`} index={g} />)}
        </ExplorationRow>,
      );
      continue;
    }
    items.push(<AssistantPart key={`part-${i}`} index={i} />);
  }

  if (running && !firstSeenAt.has(messageId)) firstSeenAt.set(messageId, Date.now());
  // When the last visible row already names the step (a running tool, streaming
  // reasoning or text, each with its own shimmer), the status line only keeps
  // time; otherwise it carries the one live label itself.
  const step = running ? liveStep(parts) : null;
  const startedMs = timing?.startedAt ? Date.parse(timing.startedAt) : NaN;

  return (
    <>
      {items}
      {running && step && (
        <TurnStatusLine
          label={step.label}
          conveyed={step.conveyed}
          startedAt={Number.isFinite(startedMs) ? startedMs : firstSeenAt.get(messageId) ?? Date.now()}
        />
      )}
    </>
  );
}

/** A tool call of a running turn that has not come back yet. */
function isInFlightTool(part: FoldPart | undefined): boolean {
  if (!part || part.type !== 'tool-call') return false;
  const p = part as FoldToolPart;
  return !p.isError && p.result === undefined && p.status?.type !== 'complete' && p.status?.type !== 'incomplete';
}

/**
 * What the running turn is doing right now, from its last part — and whether
 * that last part already says so on screen.  A running tool row, a "Thinking"
 * reasoning row and streaming text each show their own step, so the status
 * line must not repeat it ("Exploring 2 files" twice, "Thinking" twice).
 */
function liveStep(parts: readonly FoldPart[]): { label: string; conveyed: boolean } {
  const last = parts[parts.length - 1];
  if (!last) return { label: 'Thinking', conveyed: false };
  if (last.type === 'tool-call') return isInFlightTool(last) ? { label: 'Working', conveyed: true } : { label: 'Thinking', conveyed: false };
  if (last.type === 'text') return last.text.trim() ? { label: 'Writing', conveyed: true } : { label: 'Thinking', conveyed: false };
  if (last.type === 'reasoning') return { label: 'Working', conveyed: true }; // the row above says "Thinking"
  return { label: 'Working', conveyed: false };
}

/** "Worked for 4m 12s · 4 reads · 3 edits" — the settled turn's work behind one row. */
function TurnFoldRow({ fold, children }: { fold: MessageFold; children: ReactNode }) {
  const { currentThreadId } = useThreadState();
  const messageId = useAuiState((s) => s.message.id);
  const [stored, setOpen] = useFoldOpen(currentThreadId ?? '', messageId);
  const open = stored ?? fold.autoExpand;

  // A fold that opens by default gets an explicit entry, so the next run's
  // collapse closes it too.
  useEffect(() => {
    if (stored === undefined && fold.autoExpand) setOpen(true);
  }, [stored, fold.autoExpand, setOpen]);

  const summary = formatSummary(fold.summary);
  return (
    <div className="ai-turn-fold my-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="ai-turn-fold__toggle group/fold flex max-w-full items-center gap-1.5 py-1 text-meta text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        <span className="ai-turn-fold__label shrink-0">{fold.label}</span>
        {summary && <span className="ai-turn-fold__summary truncate text-zinc-400">· {summary}</span>}
        <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-zinc-300 group-hover/fold:text-zinc-500 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="ai-turn-fold__body ml-[3px] mt-1 mb-2 border-l border-zinc-200 pl-4">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * "Explored 3 files · 1 search" — consecutive reads/searches as one row.
 * While a member is in flight the label shimmers and the row carries that
 * member's Stop, which would otherwise be hidden inside the collapsed group.
 */
function ExplorationRow({ label, running, children }: {
  label: string;
  running?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ai-explore-group my-0.5">
      <div className="group/row flex min-w-0 items-center gap-1">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="ai-explore-group__toggle flex min-w-0 max-w-full items-center gap-2 py-1 text-meta text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <Search className="w-3.5 h-3.5 shrink-0 text-zinc-400" />
          <span className="ai-explore-group__label truncate">{running ? <Shimmer>{label}</Shimmer> : label}</span>
          <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-zinc-300 group-hover/row:text-zinc-500 transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="ai-explore-group__body ml-[6px] mt-0.5 mb-1 border-l border-zinc-200 pl-4">
          {children}
        </div>
      )}
    </div>
  );
}

/** Ticking "m:ss"-ish elapsed time: whole seconds, then "4m 12s". */
function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s}s` : formatDuration(s * 1000);
}

/**
 * One quiet line under a running turn.  When the last row already shows the
 * step (`conveyed`), this is just a muted verb + elapsed time; otherwise the
 * label here is the one live signal and gets the shimmer.
 */
function TurnStatusLine({ label, conveyed, startedAt }: { label: string; conveyed: boolean; startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="ai-turn-status flex items-center gap-1.5 py-1 text-meta text-zinc-400">
      <span className="ai-turn-status__label truncate">{conveyed ? label : <Shimmer>{label}</Shimmer>}</span>
      <span className="ai-turn-status__elapsed shrink-0 tabular-nums">· {formatElapsed(now - startedAt)}</span>
    </div>
  );
}

// ── Markdown Text Part ──────────────────────────────────────────────────────

/** A reply this young still gets its legend made, even if its block remounted after streaming. */
const LIVE_LEGEND_WINDOW_MS = 10 * 60_000;

function AssistantTextPart({ text, status }: { text: string; status: any }) {
  // Terms get defined once this block has finished streaming.  A block from
  // history only shows stored legends — "recent" also counts as live, because a
  // brand-new thread navigates (and remounts its messages) right as the first
  // reply finishes, which would lose a flag kept only in this component.
  const { currentThreadId } = useThreadState();
  const createdAt = turnTimestamp(useTurnTiming()); // not useMessage().createdAt: that is conversion time, "now" for history
  const streamed = useRef(status?.type === 'running');
  if (status?.type === 'running') streamed.current = true;
  const recent = !!createdAt && Date.now() - new Date(createdAt).getTime() < LIVE_LEGEND_WINDOW_MS;
  const finished = (streamed.current || recent) && status?.type !== 'running';
  const { terms, key } = useLegend(currentThreadId, status?.type === 'running' ? '' : text, finished);
  const rehypePlugins = useMemo(() => [rehypeHighlight, rehypeTerms(terms)], [terms]);
  const components = useMemo(() => ({ pre: MarkdownPre, table: MarkdownTable, span: makeTermSpan(terms, currentThreadId, key) }), [terms, currentThreadId, key]);

  return (
    <div className="ai-prose prose prose-sm prose-zinc max-w-none min-w-0 text-chat text-zinc-900 [overflow-wrap:anywhere]
      prose-headings:font-semibold prose-headings:text-zinc-900 prose-headings:tracking-normal
      prose-h1:text-lg prose-h2:text-base prose-h3:text-chat prose-h3:text-accent-700 prose-h4:text-chat prose-h4:text-accent-700
      prose-p:text-zinc-900 prose-p:leading-[1.65] prose-p:my-2.5
      prose-a:text-blue-600 prose-a:font-normal prose-a:no-underline hover:prose-a:underline prose-a:underline-offset-2
      prose-strong:text-zinc-900 prose-strong:font-semibold
      prose-code:text-accent-700 prose-code:bg-accent-50 prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:font-mono prose-code:font-normal prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none
      prose-pre:bg-transparent prose-pre:p-0 prose-pre:m-0 prose-pre:shadow-none
      prose-table:text-meta prose-table:my-0 prose-table:w-full prose-th:[overflow-wrap:normal] prose-td:[overflow-wrap:normal] prose-td:tabular-nums prose-thead:border-zinc-200 prose-thead:bg-zinc-50 prose-th:px-3 prose-th:py-1.5 prose-th:text-left prose-th:font-medium prose-th:text-zinc-600 prose-td:px-3 prose-td:py-1.5 prose-tr:border-zinc-100
      prose-li:text-zinc-900 prose-li:my-0.5 prose-li:marker:text-accent-500
      prose-blockquote:border-l-2 prose-blockquote:border-accent-200 prose-blockquote:font-normal prose-blockquote:not-italic prose-blockquote:text-zinc-600
      prose-hr:border-zinc-200"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

// ── Extended Thinking (reasoning parts) ────────────────────────────────────
//
// Reasoning streams open, Cursor-style: "Thinking" with the full text growing
// below it, no height cap or inner scrollbar, so the thoughts can be read live.
// When thinking ends it folds to one "Thought for 12s" row with a one-line
// preview.  A click during streaming sticks; otherwise the fold is automatic.

/** Last non-empty line of reasoning, as plain text (markdown markers stripped). */
function reasoningPreview(text: string): string {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const last = lines[lines.length - 1] ?? '';
  return plainText(last);
}

function AssistantReasoningPart({ text, status }: { text: string; status?: { type?: string } }) {
  const isStreaming = status?.type === 'running';
  // null = follow the stream (open while thinking, closed after); a click sets it.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? isStreaming;
  const hasText = !!text && !!text.trim();

  // Only a block seen streaming has a duration; history just says "Thought".
  const startedAt = useRef<number | null>(isStreaming ? Date.now() : null);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  useEffect(() => {
    if (isStreaming && startedAt.current === null) startedAt.current = Date.now();
    if (!isStreaming && startedAt.current !== null && durationMs === null) {
      setDurationMs(Date.now() - startedAt.current);
    }
  }, [isStreaming, durationMs]);

  if (!hasText && !isStreaming) return null;

  const preview = hasText ? reasoningPreview(text) : '';
  const doneLabel = durationMs !== null && durationMs >= 1000 ? `Thought for ${formatDuration(durationMs)}` : 'Thought';
  return (
    <div className="ai-thinking-row my-0.5">
      <button
        type="button"
        onClick={() => hasText && setUserOpen(!open)}
        aria-expanded={open}
        className="ai-thinking-row__toggle group/think flex w-full min-w-0 items-center gap-2 py-1 text-left text-meta text-zinc-600 hover:text-zinc-900 transition-colors"
      >
        <span className="ai-thinking-row__label shrink-0">{isStreaming ? <Shimmer>Thinking</Shimmer> : doneLabel}</span>
        {preview && !open && (
          <span className="ai-thinking-row__preview min-w-0 truncate text-zinc-400">{preview}</span>
        )}
        {hasText && (
          <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-zinc-300 group-hover/think:text-zinc-500 transition-transform ${open ? 'rotate-90' : ''}`} />
        )}
      </button>
      {open && hasText && (
        <div className="ai-thinking-row__body ml-[3px] mt-1 mb-2 border-l border-zinc-200 pl-4">
          <div className="text-meta leading-relaxed text-zinc-500 whitespace-pre-wrap">
            {text}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tool Call Fallback ──────────────────────────────────────────────────────

/** Neutral 14px glyph per tool family; colour comes from the row, not the tool. */
function ToolIcon({ toolName, className }: { toolName: string; className?: string }) {
  if (toolName === 'web_search' || toolName === 'fetch_url' || toolName === 'fs_fetch') return <Globe className={className} />;
  if (toolName === 'run_custom_query' || toolName === 'rest_api_call' || toolName === 'read_source') return <Database className={className} />;
  if (/_ads_/.test(toolName) || toolName === 'get_marketing_pivot') return <TrendingUp className={className} />;
  if (toolName === 'fs_read' || toolName === 'fs_ls') return <FileText className={className} />;
  if (toolName === 'fs_grep') return <Search className={className} />;
  if (toolName.startsWith('fs_')) return <Pencil className={className} />;
  if (toolName === 'generate_image' || toolName === 'generate_icon_set' || toolName === 'screenshot' || toolName === 'render_image') return <ImageIcon className={className} />;
  return <Wrench className={className} />;
}

class SafeRenderBoundary extends React.Component<
  { label?: string; children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) { console.error(`[AiChat] Render error (${this.props.label ?? 'unknown'}):`, error); }
  render() {
    if (this.state.error) {
      return (
        <div className="my-1 flex items-center gap-2 py-1">
          <XCircle className="w-3.5 h-3.5 shrink-0 text-red-500" />
          <span className="text-meta text-zinc-600">
            {this.props.label ? `"${this.props.label}" failed to render` : 'Render error'} — this does not affect the conversation.
          </span>
        </div>
      );
    }
    return this.props.children;
  }
}

function ToolCallFallbackInner({ toolName, args, result, status }: any) {
  const msg = useMessage();
  const { currentThreadId } = useThreadState();
  const messageFinished = msg.status?.type !== 'running';

  const effectiveStatus = (status?.type === 'running' && messageFinished)
    ? { type: 'incomplete' as const }
    : status;

  const isInterrupted = effectiveStatus?.type === 'incomplete' || effectiveStatus?.type === 'requires-action';
  const isRunning = effectiveStatus?.type === 'running';
  const isComplete = effectiveStatus?.type === 'complete';
  const isFailed = isComplete && result && typeof result === 'object' && result.ok === false;
  const [expanded, setExpanded] = useState(isInterrupted || isFailed);
  // The model's `_thought` for this call is the label when it wrote one (markdown
  // stripped — it is model prose); otherwise the label says what the tool did.
  const actionLabel = humanizeToolCall(toolName, args);
  const thought = plainText(args?._thought as string | undefined);

  // An HTML file is worth seeing rendered, not described. Swap the generic
  // "Writing screens/home.html" row for a card into the reviewer — unless the
  // write failed, where the error matters more than the artifact.
  const htmlTargets = extractHtmlTargets(toolName, args);
  if (htmlTargets.length > 0 && !isFailed && !isInterrupted) {
    return <HtmlArtifactCard threadId={currentThreadId} paths={htmlTargets} pending={isRunning} />;
  }

  const hasArgs = !!args && Object.keys(args).length > 0;
  const blobRef = result !== undefined ? extractBlobRef(result) : null;

  return (
    <ToolRow
      icon={
        isFailed ? <XCircle className="text-red-500" />
          : isInterrupted ? <Square className="text-yellow-500" />
          : <ToolIcon toolName={toolName} />
      }
      label={thought || actionLabel}
      title={thought ? actionLabel : undefined}
      meta={isFailed ? <span className="text-red-600">Failed</span> : isInterrupted ? <span className="text-yellow-600">Interrupted</span> : undefined}
      tone={isFailed ? 'error' : 'default'}
      running={isRunning}
      open={expanded}
      onToggle={() => setExpanded(!expanded)}
    >
      {(hasArgs || result !== undefined) && (
        <div className="space-y-1.5">
          {hasArgs && (
            <DetailPanel label="Arguments">
              <DetailPre>{safeStringify(args)}</DetailPre>
            </DetailPanel>
          )}
          {result !== undefined && (blobRef ? (
            <BlobRefView ref={blobRef} />
          ) : (
            <DetailPanel label="Result" tone={isFailed ? 'error' : 'default'}>
              <DetailPre>{typeof result === 'string' ? result : safeStringify(result)}</DetailPre>
            </DetailPanel>
          ))}
        </div>
      )}
    </ToolRow>
  );
}

function ToolCallFallback(props: any) {
  return (
    <SafeRenderBoundary label={props.toolName ?? 'tool'}>
      <ToolCallFallbackInner {...props} />
    </SafeRenderBoundary>
  );
}

function humanizeToolCall(name: string, args?: Record<string, unknown>): string {
  if (args?.path && typeof args.path === 'string') {
    const path = args.path as string;
    switch (name) {
      case 'apple_ads_get':
        return `Fetching Apple Ads: ${path}`;
      case 'apple_ads_report':
        return `Generating Apple Ads report: ${path}`;
      case 'apple_ads_mutate':
        return `Apple Ads mutation: ${args.method ?? 'POST'} ${path}`;
      case 'meta_ads_get':
        return `Fetching Meta Ads: ${path}`;
      case 'meta_ads_report':
        return `Generating Meta Ads report: ${path}`;
      case 'meta_ads_mutate':
        return `Meta Ads mutation: ${args.method ?? 'POST'} ${path}`;
      case 'tiktok_ads_get':
        return `Fetching TikTok Ads: ${path}`;
      case 'tiktok_ads_report':
        return `Generating TikTok Ads report: ${path}`;
      case 'tiktok_ads_mutate':
        return `TikTok Ads mutation: ${args.method ?? 'POST'} ${path}`;
      case 'google_ads_query':
        return 'Querying Google Ads';
      case 'google_ads_mutate':
        return `Google Ads mutation (${(args.operations as unknown[])?.length ?? 0} ops)`;
      case 'apptweak_api':
        return `AppTweak: ${path}`;
    }
  }
  if (name === 'todo_write') {
    const n = (args?.todos as unknown[])?.length ?? 0;
    return args?.merge ? `Updating todos (${n})` : `Setting todos (${n})`;
  }
  if (name === 'web_search') return `Searching the web: ${args?.query ?? ''}`;
  if (name === 'instagram_search_reels') return `Searching Instagram reels: ${args?.query ?? ''}`;
  if (name === 'instagram_search_accounts') return `Searching Instagram accounts: ${args?.query ?? ''}`;
  if (name === 'instagram_download_reel') return `Downloading reel → ${args?.saveToWorkspace ?? 'workspace'}`;
  if (name === 'run_custom_query') return 'Running BigQuery query';
  if (name === 'execute_command') return `Running: ${(args?.command as string)?.slice(0, 50) ?? 'shell command'}`;
  if (name === 'rest_api_call') return `API ${(args?.method as string) ?? 'GET'}: ${(args?.path as string) ?? ''}`;
  if (name === 'read_source') return (args?.search as string) ? `Searching source: ${args?.search as string}` : `Reading source: ${(args?.path as string) ?? 'modules'}`;
  if (name === 'generate_image') {
    const what = (args?.transparent as boolean) ? 'transparent asset' : 'image';
    const n = (args?.images as unknown[])?.length;
    if (n) return `Generating ${n} ${what}${n === 1 ? '' : 's'}`;
    return `Generating ${what}: ${(args?.prompt as string)?.slice(0, 30) ?? '...'}`;
  }
  if (name === 'generate_icon_set') {
    const n = (args?.icons as unknown[])?.length ?? 0;
    return `Generating ${n} icon${n === 1 ? '' : 's'} → ${(args?.outDir as string) ?? 'icons'}`;
  }
  if (name === 'generate_video') {
    const m = args?.model as string;
    const mLabel = m === 'omni' ? 'Omni Flash' : m === 'veo-fast' ? 'Veo Fast' : 'Veo Lite';
    return `Generating video (${mLabel}): ${(args?.prompt as string)?.slice(0, 30) ?? '...'}`;
  }
  if (name === 'check_video') return 'Checking video';
  if (name === 'fs_read') return `Reading ${args?.target_file ?? 'file'}`;
  if (name === 'fs_write') return `Writing ${args?.target_file ?? 'file'}`;
  if (name === 'fs_edit') return `Editing ${args?.target_file ?? 'file'}`;
  if (name === 'fs_delete') return `Deleting ${args?.target_file ?? 'file'}`;
  if (name === 'fs_move') return `Moving ${args?.source_file ?? 'file'} → ${args?.target_file ?? ''}`;
  if (name === 'fs_ls') return `Listing ${args?.target_directory ?? args?.glob_pattern ?? 'workspace'}`;
  if (name === 'fs_grep') return `Searching for "${(args?.pattern as string)?.slice(0, 40) ?? ''}"`;
  if (name === 'fs_batch_write') {
    const n = Object.keys((args?.files as Record<string, unknown>) ?? {}).length;
    return `Writing ${n} file${n === 1 ? '' : 's'}`;
  }
  if (name === 'fs_fetch') {
    const n = ((args?.files as unknown[]) ?? []).length;
    return `Fetching ${n} URL${n === 1 ? '' : 's'}`;
  }
  if (name === 'apply_design_preset') return `Applying ${args?.preset ?? 'design'} preset to ${args?.directory ?? 'folder'}`;
  if (name === 'scaffold_localization') return `Adding translation script to ${args?.directory ?? 'workspace'}`;
  if (name === 'scaffold_appstore_upload') return `Adding App Store uploader to ${args?.directory ?? 'workspace'}`;
  if (name === 'scaffold_play_upload') return `Adding Google Play uploader to ${args?.directory ?? 'workspace'}`;
  if (name === 'screenshot') return `Screenshotting ${args?.directory ?? 'folder'}`;
  if (name === 'batch_screenshot') return `Batch screenshot · ${((args?.shots as unknown[]) ?? []).length} shots`;
  if (name === 'render_video') return `Rendering video from ${args?.directory ?? 'folder'}`;
  if (name === 'render_image') return `Rendering image from ${args?.directory ?? 'folder'}`;
  if (name === 'fetch_url') return `Fetching ${(args?.url as string)?.slice(0, 50) ?? 'URL'}`;
  return name
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// ── Error Display ───────────────────────────────────────────────────────────

function ErrorDisplay() {
  const msg = useMessage();
  const status = msg.status;
  const errorDetail =
    status?.type === 'incomplete' && status.reason === 'error' && status.error
      ? String(status.error)
      : null;

  return (
    <div className="mt-2 flex items-start gap-2 text-meta">
      <XCircle className="w-3.5 h-3.5 shrink-0 mt-[3px] text-red-500" />
      <div className="min-w-0">
        <span className="text-zinc-900">Something went wrong.</span>
        {errorDetail && (
          <p className="mt-0.5 text-xs text-zinc-500 break-words">{errorDetail}</p>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Revert / Fork message actions (Cursor-style)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * What a scheduled restore is about to do, in a sentence.
 *
 * The count here is the CHANGE, and only in files the user can see. Reporting
 * the snapshot's own size instead meant reverting past one overwritten image
 * announced "restoring 2,678 workspace files", which reads as though the whole
 * workspace is being rewritten — and the progress chip that followed then said
 * one file, or said nothing at all.
 */
function describeRestorePlan(plan?: { copy: number; delete: number }): string {
  if (!plan) return 'restoring your workspace to this point';
  const { copy, delete: removed } = plan;
  if (copy === 0 && removed === 0) return 'your workspace already matches this point';
  const parts = [
    copy > 0 ? `putting back ${copy} file${copy === 1 ? '' : 's'}` : '',
    removed > 0 ? `removing ${removed}${copy > 0 ? '' : ` file${removed === 1 ? '' : 's'}`}` : '',
  ].filter(Boolean);
  return parts.join(' and ');
}

// Revert + fork are anchored to USER messages only: a checkpoint is captured at
// the start of each user turn, so a user message is the only point whose
// workspace snapshot can be cleanly restored (revert) or copied (fork).
function RevertForkActions() {
  const msg = useMessage();
  const messageId = msg.id;
  const {
    currentThreadId,
    reloadCurrentThread,
    refreshThreads,
    switchThread,
    setPendingComposerText,
  } = useThreadState();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<null | 'revert' | 'revert-ws' | 'fork'>(null);

  // No message id (ephemeral panel / unsaved thread) → nothing to target.
  if (!currentThreadId || !messageId) return null;

  const doRevert = async (restoreWorkspace: boolean) => {
    setBusy(restoreWorkspace ? 'revert-ws' : 'revert');
    try {
      const res = await threadApi.revert(currentThreadId, { messageId, restoreWorkspace });
      setPendingComposerText(res.revertedText || null);
      reloadCurrentThread();
      refreshThreads();
      // Say what actually happened to the files.  A workspace restore deletes
      // everything created after the snapshot, and it used to report nothing at
      // all — including when it silently declined to restore.
      if (!restoreWorkspace) {
        toast.info(`Reverted to this message — workspace files untouched`);
      } else if (res.workspaceRestorePending) {
        toast.success(`Reverted — ${describeRestorePlan(res.restorePlan)}`);
      } else if (res.restoreSkipped === 'stale-snapshot') {
        toast.error('Reverted the chat only: the snapshot at this message belongs to a branch a previous revert discarded, so the files were left as they are.');
      } else if (res.restoreSkipped === 'suspect-snapshot') {
        toast.error('Reverted the chat only: the snapshot at this message was taken while the file sync was unhealthy, so restoring it would impose a state your workspace was never in. The files were left as they are.');
      } else {
        toast.error('Reverted the chat only: no workspace snapshot was captured at this message, so the files were left as they are.');
      }
    } catch (err) {
      reportSurgeryFailure(err, {
        threadId: currentThreadId,
        fallback: 'Revert failed — nothing was changed',
        retry: () => void doRevert(restoreWorkspace),
      });
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };

  const doFork = async () => {
    setBusy('fork');
    try {
      const res = await threadApi.fork(currentThreadId, { messageId });
      refreshThreads();
      switchThread(res.id);
    } catch (err) {
      reportSurgeryFailure(err, {
        threadId: currentThreadId,
        fallback: 'Fork failed — no new chat was created',
        retry: () => void doFork(),
      });
    } finally {
      setBusy(null);
    }
  };

  const iconBtn = MSG_ACTION;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={iconBtn}
        title="Revert to here"
      >
        <RotateCcw className="w-3.5 h-3.5" />
      </button>

      {open && (
        <RevertScopeDialog
          title="Revert to this message"
          busy={busy === 'revert' ? 'chat' : busy === 'revert-ws' ? 'workspace' : null}
          onChoose={doRevert}
          onClose={() => setOpen(false)}
        />
      )}

      <button
        type="button"
        onClick={doFork}
        disabled={busy === 'fork'}
        className={iconBtn}
        title="Fork from here into a new chat"
      >
        {busy === 'fork' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitBranch className="w-3.5 h-3.5" />}
      </button>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Branch Picker
// ═══════════════════════════════════════════════════════════════════════════════

function BranchPicker() {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className="inline-flex items-center gap-0.5"
    >
      <BranchPickerPrimitive.Previous className="w-6 h-6 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 disabled:opacity-30 transition-colors">
        <ChevronLeft className="w-3.5 h-3.5" />
      </BranchPickerPrimitive.Previous>
      <span className="text-xs text-zinc-500 tabular-nums min-w-[36px] text-center">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next className="w-6 h-6 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 disabled:opacity-30 transition-colors">
        <ChevronRight className="w-3.5 h-3.5" />
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Chat Composer
// ═══════════════════════════════════════════════════════════════════════════════

function ChatComposer() {
  const { pendingComposerText, setPendingComposerText, currentThreadId: composerThreadId } = useThreadState();

  // After a revert, the runtime remounts and this composer re-mounts with it;
  // seed the reverted prompt back into the input so the user can edit/resend
  // (Cursor-style).  Consume-once so a later remount doesn't re-inject it.
  let aui: ReturnType<typeof useAui> | null = null;
  try { aui = useAui(); } catch { /* not inside runtime */ }
  useEffect(() => {
    if (pendingComposerText == null) return;
    try { aui?.composer().setText(pendingComposerText); } catch { /* noop */ }
    setPendingComposerText(null);
  }, [pendingComposerText, aui, setPendingComposerText]);

  return (
    <div className="ai-composer shrink-0 bg-white px-4 pb-3 pt-1 sm:px-6">
      <div className="mx-auto w-full max-w-3xl">
      <BackgroundTasksBar threadId={composerThreadId} />
      <ComposerPrimitive.Root className="relative flex flex-col rounded-lg border border-zinc-200 bg-white transition-[border-color,box-shadow] duration-150 focus-within:border-zinc-400 focus-within:ring-2 focus-within:ring-zinc-900/5">
        <div className="flex flex-wrap gap-2 px-3 pt-3 empty:hidden">
          <ComposerPrimitive.Attachments
            components={{ Attachment: ComposerAttachment }}
          />
        </div>

        <div className="flex items-end gap-1.5 px-2 py-1.5">
          <ComposerPrimitive.AddAttachment title="Attach files" className="w-8 h-8 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors shrink-0">
            <Paperclip className="w-4 h-4" />
          </ComposerPrimitive.AddAttachment>

          <ComposerPrimitive.Input
            placeholder="Ask anything"
            className="flex-1 resize-none bg-transparent text-chat text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 min-h-[32px] max-h-[160px] py-1"
            autoFocus
          />

          <ThreadPrimitive.If running={false}>
            <ComposerPrimitive.Send title="Send" className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:bg-zinc-100 disabled:text-zinc-400 disabled:cursor-not-allowed transition-colors shrink-0">
              <ArrowUp className="w-4 h-4" />
            </ComposerPrimitive.Send>
          </ThreadPrimitive.If>

          <ThreadPrimitive.If running>
            {/*
              Ends this turn only. Subthreads keep going — they have their own
              budget, and Stop is used as much to cut a long-winded turn short as
              to abandon the job. Stopping them is a separate control, in the
              cost breakdown.
            */}
            <ComposerPrimitive.Cancel
              title="Stop this turn (subthreads keep running)"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-zinc-900 text-white hover:bg-zinc-700 transition-colors shrink-0"
            >
              <Square className="w-3 h-3 fill-current" />
            </ComposerPrimitive.Cancel>
          </ThreadPrimitive.If>
        </div>
      </ComposerPrimitive.Root>

      <div className="mt-1 flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-0.5 -ml-1.5">
          <ModelSelector />
          <EffortSelector />
          <ToolPicker />
          <ThreadCostBadge />
          <ThreadContextBadge />
          <MacUsageBadge />
          <CompactionWidget />
          <WorkspaceRestoreWidget />
          <SandboxWidget />
        </div>
      </div>
      </div>
    </div>
  );
}

/** A small provider-coloured dot beside a model name. */
const PROVIDER_DOT: Record<string, string> = { google: 'bg-blue-500', openai: 'bg-emerald-500', anthropic: 'bg-accent-500', 'mac-claude': 'bg-zinc-700' };
function ProviderDot({ provider }: { provider: string }) {
  return <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${PROVIDER_DOT[provider] ?? 'bg-zinc-400'}`} />;
}

/** The quiet text buttons under the composer: model, tools, cost, context…
 *  Each carries one small muted icon in its own colour; the text stays zinc. */
const CHIP =
  'inline-flex items-center gap-1 h-6 px-1.5 rounded-md text-xs text-zinc-500 tabular-nums hover:text-zinc-900 hover:bg-zinc-100 transition-colors';
/** Same shape for a non-interactive status chip. */
const CHIP_STATIC = 'inline-flex items-center gap-1 h-6 px-1.5 text-xs text-zinc-500 tabular-nums';

interface ThreadCostData {
  costUsd: number;
  mainCostUsd: number;
  summaryCostUsd: number;
  turnCount: number;
  summaryTurnCount: number;
  hasUnpricedTurns: boolean;
  byModel: Array<{ modelId: string; costUsd: number; turns: number }>;
  /**
   * Included in `costUsd`: spend on turns that were reverted away or re-run.
   * Those tokens were billed when the provider answered, so they stay in the
   * total even though the conversation no longer contains them.
   */
  discardedCostUsd?: number;
  discardedTurnCount?: number;
  /** External model-API spend (fal / Gemini vision / Veo / Grok / image gen / ElevenLabs) via tool calls. */
  external?: {
    costUsd: number;
    callCount: number;
    hasEstimates: boolean;
    byProvider: Array<{ provider: string; costUsd: number; calls: number }>;
    byTool: Array<{ toolName: string; costUsd: number; calls: number }>;
  } | null;
  /**
   * Spend by the hidden child threads this one fanned out to.  Nearly all of a
   * fan-out's cost lives here rather than in the parent's own turns, and the
   * children are filtered out of the sidebar, so leaving it out of the total
   * would make parallel work look almost free.
   */
  subthreads?: {
    count: number;
    /** Children that have not settled, i.e. are still spending. */
    liveCount: number;
    costUsd: number;
    llmCostUsd: number;
    externalCostUsd: number;
    hasUnpricedTurns: boolean;
    children: Array<{
      threadId: string;
      label?: string;
      costUsd: number;
      turnCount: number;
      state?: string;
      live?: boolean;
    }>;
  };
}

function formatUsd(amount: number): string {
  if (amount <= 0) return '$0.00';
  if (amount < 0.01) return '<$0.01';
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
}

/**
 * Single chip with the thread's total estimated spend — LLM token usage plus
 * external model-API tool calls (fal / Gemini vision / Veo / Grok / image gen /
 * ElevenLabs). Click opens the full breakdown. Refreshes after each turn and
 * whenever a tool cost event is persisted (both re-emit the `cost` signal).
 */
function ThreadCostBadge() {
  const { currentThreadId, threadVersion } = useThreadState();
  // `tick` bumps when a turn's cost telemetry is persisted, so we refetch the
  // instant it's available rather than racing the server persist. `inFlightUsd`
  // is the running spend of the turn currently streaming, which no persisted
  // figure can include yet.
  const { tick: costSignal, inFlightUsd } = useCostSignal(currentThreadId);
  const [data, setData] = useState<ThreadCostData | null>(null);
  const [open, setOpen] = useState(false);
  // Refetched on demand as well as on signal, because stopping the children is
  // the one thing that changes this chip from inside this component. The server
  // emits a `cost` signal for it too, but that only arrives while the event
  // stream is connected — and a chip that goes on saying "13 running" after
  // they have been stopped reads as a stop that did nothing.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!currentThreadId) {
      setData(null);
      return;
    }
    let cancelled = false;
    api
      .get<ThreadCostData>(`/chat/threads/${currentThreadId}/cost`)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentThreadId, threadVersion, costSignal, reloadKey]);

  const externalCost = data?.external?.costUsd ?? 0;
  const subthreadCost = data?.subthreads?.costUsd ?? 0;
  const persistedUsd = (data?.costUsd ?? 0) + externalCost + subthreadCost;
  const totalUsd = persistedUsd + inFlightUsd;
  const live = data?.subthreads?.liveCount ?? 0;
  // Live children keep the chip up even at $0, because the breakdown behind it
  // is where a running fan-out is stopped and the first minute — before any
  // child's telemetry has landed — is exactly when you might want that.
  if (!data || (totalUsd <= 0 && live === 0)) return null;

  const approximate = data.hasUnpricedTurns
    || !!data.external?.hasEstimates
    || !!data.subthreads?.hasUnpricedTurns;

  // Both kinds of "still going" are worth saying, and they are independent: a
  // fan-out spends while this thread sits idle, and this thread spends while
  // every child has settled.
  const title = [
    'Estimated thread spend',
    ...(inFlightUsd > 0 ? [`${formatUsd(inFlightUsd)} of it is the turn still running`] : []),
    ...(live > 0 ? [`${live} subthread${live === 1 ? '' : 's'} still running`] : []),
    'click for the breakdown',
  ].join(' — ');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={title}
        // Amber outranks emerald: an in-flight turn is spending in front of you
        // and ends on its own, while live children spend out of sight and are
        // the thing you might want to go and stop.
        className={CHIP}
      >
        <DollarSign className="w-3 h-3 text-emerald-600" />
        {approximate ? '~' : ''}
        {formatUsd(totalUsd)}
        {live > 0 && <span className="text-accent-600">· {live} running</span>}
      </button>
      {open && (
        <CostBreakdownModal
          data={data}
          totalUsd={totalUsd}
          inFlightUsd={inFlightUsd}
          onStopped={() => setReloadKey((n) => n + 1)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

interface ContextCategory {
  id: string;
  label: string;
  tokens: number;
  basis: 'counted' | 'billed' | 'derived';
  children?: ContextCategory[];
  detail?: string;
}

interface ContextBreakdownData {
  totalTokens: number;
  ceilingTokens: number;
  modelId: string;
  categories: ContextCategory[];
  measuredAt: string;
  unavailableReason?: string;
  nextCompaction?: NextCompaction;
}

/**
 * What is about to compact this thread, which is rarely the ceiling. Once a
 * thread owns a running summary the prompt's size stops being the question and
 * the delta piling up past that summary is what trips — so the pill has to be
 * able to say so, or it reads as a contradiction at half-full.
 */
interface NextCompaction {
  rule: 'first-summary' | 'summary-refresh' | 'mid-turn';
  current: number;
  at: number;
  label: string;
}

interface ContextPillData {
  totalTokens: number;
  ceilingTokens: number;
  nextCompaction?: NextCompaction;
}

function formatTokens(n: number): string {
  if (n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}K`;
  return `${Math.round(n / 1000)}K`;
}

interface PlanWindow { used: number; resetsAt: string | null }
interface PlanLimits {
  plan: string | null;
  fiveHour: PlanWindow | null;
  weekly: PlanWindow | null;
  models: Array<PlanWindow & { name: string }>;
  at: string;
}
interface MacUsageData {
  thread: { fiveHour: number; weekly: number; models: Record<string, number>; turns: number } | null;
  account: PlanLimits | null;
  accountError?: string;
}

/** A plan percentage: whole numbers, with "<1%" for a thread that used some but not a full point. */
function planPct(n: number, turns = 0): string {
  return n === 0 && turns > 0 ? '<1%' : `${Math.round(n)}%`;
}

function resetsIn(at: string | null): string {
  if (!at) return '';
  const d = new Date(at);
  const sameDay = d.toDateString() === new Date().toDateString();
  return `resets ${sameDay ? '' : `${d.toLocaleDateString(undefined, { weekday: 'short' })} `}${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * The Claude subscription, for threads on the Mac Claude models: the share of
 * the plan's weekly limit this thread has used, and behind it the account's
 * limits now (the 5-hour window, the week, per-model weeks). The thread's
 * share is measured in whole percents before and after each turn, so it is
 * approximate.
 */
function MacUsageBadge() {
  const { selectedModel } = useModelSelection();
  const { currentThreadId, threadVersion } = useThreadState();
  const usageSignal = useMacUsageSignal();
  const [data, setData] = useState<MacUsageData | null>(null);
  const [open, setOpen] = useState(false);
  const onMac = AI_MODELS.find((m) => m.id === selectedModel)?.provider === 'mac-claude';

  useEffect(() => {
    if (!onMac) return;
    let cancelled = false;
    api
      .get<MacUsageData>('/mac-claude/usage', currentThreadId ? { threadId: currentThreadId } : {})
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [onMac, currentThreadId, threadVersion, usageSignal, open]);

  if (!onMac || !data) return null;
  const thread = data.thread;
  const turns = thread?.turns ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`About ${planPct(thread?.weekly ?? 0, turns)} of the Claude subscription's weekly limit went to this thread — click for the account's limits`}
        className={CHIP}
      >
        <ChartPie className="w-3 h-3 text-zinc-600" />
        {planPct(thread?.weekly ?? 0, turns)}
      </button>
      {open && <MacUsageModal data={data} onClose={() => setOpen(false)} />}
    </>
  );
}

function MacUsageModal({ data, onClose }: { data: MacUsageData; onClose: () => void }) {
  const { thread, account } = data;
  const turns = thread?.turns ?? 0;

  const row = (label: string, used: number | null, note: string) => (
    <div key={label} className="py-2">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="text-zinc-700">{label}</span>
        <span className="text-zinc-500 tabular-nums whitespace-nowrap">
          {used === null ? '—' : <span className="font-semibold text-zinc-800">{Math.round(used)}%</span>}
          {note && <span className="ml-2 text-zinc-400">{note}</span>}
        </span>
      </div>
      <div className="h-1 rounded-full bg-zinc-100 overflow-hidden mt-1">
        <div
          className={`h-full rounded-full ${(used ?? 0) >= 90 ? 'bg-red-500' : (used ?? 0) >= 75 ? 'bg-yellow-500' : 'bg-zinc-600'}`}
          style={{ width: `${Math.min(100, used ?? 0)}%` }}
        />
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/20" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-[86vh] flex flex-col rounded-lg bg-white shadow-lg border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <ChartPie className="w-4 h-4 text-zinc-500" /> Claude subscription
              {account?.plan && <span className="text-xs font-normal text-zinc-400 capitalize">{account.plan}</span>}
            </h2>
            <p className="text-xs text-zinc-500 mt-1">Usage limits of the plan the Mac models run on.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-3 divide-y divide-zinc-100">
          <div className="pb-3">
            <div className="text-xs font-semibold text-zinc-900 mb-1">This thread</div>
            {turns === 0 ? (
              <div className="text-xs text-zinc-500">No turns on a Mac model yet.</div>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_auto] gap-y-1 text-xs">
                  <span className="text-zinc-600">Weekly limit</span>
                  <span className="font-semibold text-zinc-800 tabular-nums text-right">{planPct(thread!.weekly, turns)}</span>
                  <span className="text-zinc-600">5-hour limit</span>
                  <span className="text-zinc-700 tabular-nums text-right">{planPct(thread!.fiveHour, turns)}</span>
                  {Object.entries(thread!.models).map(([name, n]) => (
                    <React.Fragment key={name}>
                      <span className="text-zinc-600">{name} weekly limit</span>
                      <span className="text-zinc-700 tabular-nums text-right">{planPct(n, turns)}</span>
                    </React.Fragment>
                  ))}
                </div>
                <p className="text-xs text-zinc-400 mt-2">
                  Over {turns} turn{turns === 1 ? '' : 's'}. Approximate: the limits move in whole percents, and other Claude
                  use at the same time (a terminal session, another thread) counts too.
                </p>
              </>
            )}
          </div>
          <div className="pt-3">
            <div className="text-xs font-semibold text-zinc-900">The account now</div>
            {data.accountError && <div className="text-xs text-yellow-700 mt-1">{data.accountError}{account ? ' Showing the last reading.' : ''}</div>}
            {account ? (
              <>
                {row('5-hour limit', account.fiveHour ? account.fiveHour.used : 0, account.fiveHour ? resetsIn(account.fiveHour.resetsAt) : 'starts with the next message')}
                {row('Weekly limit', account.weekly ? account.weekly.used : null, resetsIn(account.weekly?.resetsAt ?? null))}
                {account.models.map((m) => row(`${m.name} weekly limit`, m.used, resetsIn(m.resetsAt)))}
                <p className="text-xs text-zinc-400 pt-1">
                  There is no daily limit: the plan has a 5-hour window that starts with the first message after it ends, and weekly limits.
                  Read {new Date(account.at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}.
                </p>
              </>
            ) : (
              !data.accountError && <div className="text-xs text-zinc-500 mt-1">Not available.</div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * How close this thread is to compacting, and what is taking up the room.
 *
 * Shows tokens against the mid-turn compaction ceiling rather than bytes,
 * because the ceiling, the trigger and the bill are all denominated in tokens —
 * bytes would be a number you still have to convert before it tells you
 * anything. Colour is the actual signal: the point of the pill is to make "you
 * are one step from a compaction" visible before it happens rather than after.
 *
 * Mid-turn the number comes from the live per-step signal (the provider's own
 * input-token count); between turns it falls back to the figure persisted for
 * the last request.
 */
function ThreadContextBadge() {
  const { currentThreadId, threadVersion } = useThreadState();
  const live = useContextSignal(currentThreadId);
  const [persisted, setPersisted] = useState<ContextPillData | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!currentThreadId) {
      setPersisted(null);
      return;
    }
    let cancelled = false;
    api
      .get<ContextPillData>(`/chat/threads/${currentThreadId}/context`)
      .then((res) => { if (!cancelled) setPersisted(res); })
      .catch(() => { if (!cancelled) setPersisted(null); });
    return () => { cancelled = true; };
  }, [currentThreadId, threadVersion, live?.totalTokens]);

  const shown = live ?? persisted;
  if (!shown || shown.totalTokens <= 0) return null;

  const { totalTokens, ceilingTokens } = shown;
  // The live SSE signal carries only the gauge, so the trigger always comes
  // from the fetched record.
  const next = persisted?.nextCompaction;
  const ratio = ceilingTokens > 0 ? totalTokens / ceilingTokens : 0;
  // The ceiling is not the only thing that compacts a thread, and on anything
  // with a running summary it is not even the likely one — so the colour
  // tracks whichever rule is closest to firing, while the number keeps
  // reporting occupancy.  Without this a thread re-summarizing every turn
  // still looked calm at a third of its ceiling.
  const nextRatio = next && next.at > 0 ? next.current / next.at : 0;
  const worst = Math.max(ratio, nextRatio);
  // Amber starts at 75% because that is roughly where a single large tool
  // result is enough to cross the ceiling, i.e. where it stops being a
  // statistic and starts being a prediction.
  // Colour on the text only, and only when it is a warning.
  const tone = worst >= 0.9 ? 'text-red-600' : worst >= 0.75 ? 'text-yellow-600' : '';

  const title = [
    `Context: ${totalTokens.toLocaleString()} of ${ceilingTokens.toLocaleString()} tokens` +
      ` — ${live ? 'live, from the turn running now' : 'from the last completed turn'}`,
    next
      ? `Next compaction: ${next.current.toLocaleString()} of ${next.at.toLocaleString()} — ${next.label}`
      : null,
    'Click for the measured breakdown',
  ].filter(Boolean).join('\n');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={title}
        className={CHIP}
      >
        <Gauge className="w-3 h-3 text-cyan-600" />
        <span className={tone}>{formatTokens(totalTokens)} / {formatTokens(ceilingTokens)}</span>
      </button>
      {open && <ContextBreakdownModal onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Where the context window is actually going, measured.
 *
 * Every number here is priced by the provider's own tokenizer rather than
 * estimated from character counts — which matters more than it sounds: against
 * the real counter, `chars/4` understated this thread's tool schemas by about a
 * factor of two, i.e. it was wrong about the single biggest row by more than
 * the conversation's entire size.
 */
function ContextBreakdownModal({ onClose }: { onClose: () => void }) {
  const { currentThreadId } = useThreadState();
  const [data, setData] = useState<ContextBreakdownData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tools, setTools] = useState<{ tools: Array<{ name: string; tokens: number }>; framingTokens: number } | null>(null);
  const [toolsLoading, setToolsLoading] = useState(false);

  useEffect(() => {
    if (!currentThreadId) return;
    let cancelled = false;
    api
      .get<ContextBreakdownData>(`/chat/threads/${currentThreadId}/context`, { breakdown: '1' }, 60_000)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setError('Could not measure the context for this thread.'); });
    return () => { cancelled = true; };
  }, [currentThreadId]);

  const loadTools = useCallback(async () => {
    if (!currentThreadId || tools || toolsLoading) return;
    setToolsLoading(true);
    try {
      // Generous: one count-tokens call per active tool, and a wide selection
      // is ~90 of them.
      setTools(await api.get(`/chat/threads/${currentThreadId}/context`, { tools: '1' }, 120_000));
    } catch {
      /* the row stays collapsed; the total above is unaffected */
    } finally {
      setToolsLoading(false);
    }
  }, [currentThreadId, tools, toolsLoading]);

  const total = data?.totalTokens ?? 0;
  const ceiling = data?.ceilingTokens ?? 0;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

  const bar = (n: number, tone: string) => (
    <div className="h-1 rounded-full bg-zinc-100 overflow-hidden mt-1">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, pct(n))}%` }} />
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/20" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-[86vh] flex flex-col rounded-lg bg-white shadow-lg border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <Gauge className="w-4 h-4 text-zinc-500" /> Context window
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              {data ? (
                <>
                  <span className="font-semibold text-zinc-700 tabular-nums">{total.toLocaleString()}</span>
                  {' of '}
                  <span className="tabular-nums">{ceiling.toLocaleString()}</span>
                  {' tokens before compaction'}
                  {ceiling > 0 && <> · <span className="tabular-nums">{Math.round((total / ceiling) * 100)}%</span></>}
                </>
              ) : 'Measuring…'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <div className="text-xs text-zinc-500">{error}</div>}

          {!data && !error && (
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Pricing each block with the provider's token counter…
            </div>
          )}

          {data?.unavailableReason && (
            <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2">
              {data.unavailableReason}
            </div>
          )}

          {/* Stated before the breakdown, because the percentage above answers
              "how full is it" and this answers "so when does it compact" —
              which are different questions with different thresholds, and
              reading only the first one is what makes a half-full thread
              compacting look like a bug. */}
          {data?.nextCompaction && (
            <div className="rounded-lg bg-zinc-50 border border-zinc-100 px-3 py-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-zinc-600">Next compaction</div>
                <div className="text-xs text-zinc-600 tabular-nums shrink-0 pl-3">
                  {data.nextCompaction.current.toLocaleString()} / {data.nextCompaction.at.toLocaleString()}
                </div>
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">{data.nextCompaction.label}</div>
            </div>
          )}

          {data?.categories.map((c) => (
            <div key={c.id}>
              <div className="flex items-center justify-between">
                <div className="text-xs text-zinc-700 flex items-center gap-1.5">
                  {c.label}
                  {c.basis === 'derived' && (
                    <span className="text-xs text-zinc-400 font-normal">derived</span>
                  )}
                </div>
                <div className="text-xs text-zinc-700 tabular-nums shrink-0 pl-3">
                  {formatTokens(c.tokens)}
                  <span className="text-zinc-400 font-normal"> · {pct(c.tokens).toFixed(0)}%</span>
                </div>
              </div>
              {bar(c.tokens, c.basis === 'derived' ? 'bg-zinc-300' : 'bg-zinc-500')}
              {c.detail && <div className="text-xs text-zinc-400 mt-1">{c.detail}</div>}

              {c.children && c.children.length > 0 && (
                <div className="mt-1.5 pl-3 border-l border-zinc-100 space-y-1">
                  {c.children.filter((ch) => ch.tokens > 0).map((ch) => (
                    <div key={ch.id} className="flex items-center justify-between">
                      <div className="text-xs text-zinc-500 truncate">{ch.label}</div>
                      <div className="text-xs text-zinc-500 tabular-nums shrink-0 pl-3">{formatTokens(ch.tokens)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* The tool row is the one worth drilling into: it is usually the
                  largest, and the tool SELECTION is the only part of the static
                  prefix a user can actually change. */}
              {c.id === 'tools' && (
                <div className="mt-1.5">
                  {!tools ? (
                    <button
                      type="button"
                      onClick={loadTools}
                      disabled={toolsLoading}
                      className="text-xs text-zinc-500 hover:text-zinc-700 underline decoration-dotted disabled:opacity-60"
                    >
                      {toolsLoading ? 'Measuring each tool…' : 'Break down by tool'}
                    </button>
                  ) : (
                    <div className="pl-3 border-l border-zinc-100 space-y-1">
                      {tools.tools.slice(0, 12).map((t) => (
                        <div key={t.name} className="flex items-center justify-between">
                          <div className="text-xs text-zinc-500 truncate font-mono">{t.name}</div>
                          <div className="text-xs text-zinc-500 tabular-nums shrink-0 pl-3">{formatTokens(t.tokens)}</div>
                        </div>
                      ))}
                      {tools.tools.length > 12 && (
                        <div className="text-xs text-zinc-400">
                          + {tools.tools.length - 12} more, {formatTokens(tools.tools.slice(12).reduce((s, t) => s + t.tokens, 0))} between them
                        </div>
                      )}
                      <div className="text-xs text-zinc-400 pt-0.5">
                        Deselecting a tool in the picker removes its schema from every turn.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {data && !data.unavailableReason && (
            <div className="text-xs text-zinc-400 border-t border-zinc-100 pt-3">
              Every row above is counted by the provider's own tokenizer, not estimated. The total is
              what it charged for the last request; the conversation row is that total minus the
              measured blocks.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Abandon a thread's background work, deliberately.
 *
 * The only control that stops a fan-out, and the reason Stop no longer does:
 * ending a turn and abandoning children spending their own budget are separate
 * decisions, and conflating them once cost a 19-way fan-out most of its work.
 * Two clicks deep and confirmed, because the mistake it guards against is
 * expensive and irreversible.
 */
function StopSubthreadsButton({ threadId, liveCount, onStopped }: {
  threadId: string | null;
  liveCount: number;
  onStopped: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<number | null>(null);

  const stop = useCallback(async () => {
    if (!threadId) return;
    setBusy(true);
    try {
      // Generous timeout: each child gets up to 30s to write its own account of
      // how it ended before its container goes, so the default 30s would report
      // a failure on a stop that in fact succeeded.
      const res = await api.post<{ stopped: number }>('/chat/subthreads/stop', { threadId }, 90_000);
      setResult(res?.stopped ?? 0);
    } catch {
      setResult(-1);
    } finally {
      setBusy(false);
      setConfirming(false);
      // Either way: a request that timed out client-side may well have stopped
      // them server-side, so the count comes from a refetch rather than from
      // whether this call returned.
      onStopped();
    }
  }, [threadId, onStopped]);

  // Shown alongside the control rather than instead of it, so a fan-out spawned
  // after a stop still gets a button, and a stop whose request timed out does
  // not claim a failure the refetched count may well contradict.
  const receipt = result === null ? null : (
    <div className="mt-2 text-xs text-zinc-500">
      {result < 0
        ? 'The request did not come back in time — the count above is refetched, so trust that.'
        : `Stopped ${result} subthread${result === 1 ? '' : 's'}. Their files stay in place; a follow-up can resume them.`}
    </div>
  );

  if (confirming) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={stop}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 disabled:opacity-60 transition-colors"
        >
          <Square className="w-3 h-3" />
          {busy ? 'Stopping…' : `Yes, stop ${liveCount}`}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirming(false)}
          className="px-2.5 py-1 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-500 hover:bg-zinc-50 disabled:opacity-60 transition-colors"
        >
          Keep them running
        </button>
      </div>
    );
  }

  if (liveCount === 0) return receipt;

  return (
    <>
      {receipt}
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-600 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
      >
        <Square className="w-3 h-3" />
        Stop {liveCount} running subthread{liveCount === 1 ? '' : 's'}
      </button>
    </>
  );
}

function CostBreakdownModal({
  data,
  totalUsd,
  inFlightUsd,
  onStopped,
  onClose,
}: {
  data: ThreadCostData;
  totalUsd: number;
  inFlightUsd: number;
  /** Ask the badge to refetch, so a stop is visible in it immediately. */
  onStopped: () => void;
  onClose: () => void;
}) {
  const { currentThreadId } = useThreadState();
  const external = data.external;
  const hasExternal = !!external && external.costUsd > 0;
  const subthreads = data.subthreads;
  const hasSubthreads = !!subthreads && subthreads.count > 0;
  const hasSplit = typeof data.summaryCostUsd === 'number' && data.summaryCostUsd > 0;
  const discardedUsd = data.discardedCostUsd ?? 0;

  const row = (label: string, sub: string, cost: number) => (
    <div key={label} className="flex items-center justify-between py-1.5">
      <div className="min-w-0">
        <div className="text-xs text-zinc-700 truncate">{label}</div>
        <div className="text-xs text-zinc-400">{sub}</div>
      </div>
      <div className="text-xs text-zinc-700 tabular-nums shrink-0 pl-3">{formatUsd(cost)}</div>
    </div>
  );

  const sectionHeader = (icon: ReactNode, label: string, cost: number) => (
    <div className="flex items-center justify-between mb-1">
      <h3 className="text-xs font-medium text-zinc-500 flex items-center gap-1.5">
        {icon}
        {label}
      </h3>
      <span className="text-xs font-medium text-zinc-700 tabular-nums">{formatUsd(cost)}</span>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/20" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-[86vh] flex flex-col rounded-lg bg-white shadow-lg border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-zinc-500" /> Thread cost
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Total estimated spend: <span className="font-semibold text-zinc-700 tabular-nums">{formatUsd(totalUsd)}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {data.costUsd > 0 && (
            <div>
              {sectionHeader(null, 'LLM usage', data.costUsd)}
              <div className="divide-y divide-zinc-100">
                {data.byModel.map((m) =>
                  row(m.modelId, `${m.turns} turn${m.turns === 1 ? '' : 's'}`, m.costUsd),
                )}
              </div>
              {hasSplit && (
                <div className="text-xs text-zinc-400 mt-1.5">
                  Main {formatUsd(data.mainCostUsd)} · Compaction {formatUsd(data.summaryCostUsd)} (
                  {data.summaryTurnCount} summar{data.summaryTurnCount === 1 ? 'y' : 'ies'})
                </div>
              )}
              {discardedUsd > 0 && (
                <div className="text-xs text-zinc-400 mt-1.5">
                  Includes {formatUsd(discardedUsd)} for {data.discardedTurnCount ?? 0} turn
                  {(data.discardedTurnCount ?? 0) === 1 ? '' : 's'} you reverted or re-ran — billed when
                  they ran, so they stay in the total.
                </div>
              )}
            </div>
          )}

          {inFlightUsd > 0 && (
            <div className="text-xs text-zinc-500 flex items-center gap-1.5">
              {formatUsd(inFlightUsd)} of this is the turn still running; it settles into the totals
              above when the turn ends.
            </div>
          )}

          {hasExternal && (
            <div>
              {sectionHeader(<Zap className="w-3 h-3" />, 'External model APIs', external!.costUsd)}
              <div className="divide-y divide-zinc-100">
                {external!.byTool.map((t) =>
                  row(t.toolName, `${t.calls} call${t.calls === 1 ? '' : 's'}`, t.costUsd),
                )}
              </div>
              <div className="text-xs text-zinc-400 mt-1.5">
                By provider: {external!.byProvider.map((p) => `${p.provider} ${formatUsd(p.costUsd)}`).join(' · ')}
              </div>
            </div>
          )}

          {hasSubthreads && (
            <div>
              {sectionHeader(<GitBranch className="w-3 h-3" />, 'Subthreads', subthreads!.costUsd)}
              <div className="divide-y divide-zinc-100">
                {subthreads!.children.map((c) =>
                  row(
                    c.label ?? c.threadId.slice(0, 8),
                    `${c.turnCount} turn${c.turnCount === 1 ? '' : 's'}${c.state ? ` · ${c.state}` : ''}`,
                    c.costUsd,
                  ),
                )}
              </div>
              <div className="text-xs text-zinc-400 mt-1.5">
                {subthreads!.count} background thread{subthreads!.count === 1 ? '' : 's'} · LLM{' '}
                {formatUsd(subthreads!.llmCostUsd)} · External {formatUsd(subthreads!.externalCostUsd)}
              </div>
              <StopSubthreadsButton
                threadId={currentThreadId}
                liveCount={subthreads!.liveCount}
                onStopped={onStopped}
              />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-100 text-xs text-zinc-400 leading-relaxed">
          Estimates based on public list prices — not an invoice.
          {data.hasUnpricedTurns && ' Some turns used a model with no pricing data, so the real total may be higher.'}
          {external?.hasEstimates &&
            ' External spend includes per-image/second/character heuristics — treat as approximate.'}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Live context-compaction status, driven by the `summary` SSE events.  While
 * the model pauses to summarize earlier context (at a turn start or mid-turn
 * between tool steps) it shows an animated "Compacting context…" pill; once
 * done it collapses to a clickable chip that opens what the model now sees in
 * place of the older messages.  Session-scoped (no persistence) — the durable
 * summary still shapes context across reloads; we just don't re-surface a pill.
 */
function CompactionWidget() {
  const { active, scope, last } = useSummaryProgress();
  const running = useAuiState((s: any) => s.thread?.isRunning ?? false);
  const [open, setOpen] = useState(false);
  // Stored state, so the chip survives a reload; each finished live pass refetches.
  const { currentThreadId } = useThreadState();
  const view = useSummaryView(currentThreadId, last);
  const passes = view?.history.length ?? 0;

  // Only show the live "compacting" pill while the turn is actually running, so
  // a dropped SSE connection between `start` and `done` can't leave it stuck.
  if (active && running) {
    return (
      <span className={CHIP_STATIC}>
        <Shimmer>Compacting context{scope === 'mid-turn' ? ' (mid-task)' : ''}</Shimmer>
      </span>
    );
  }

  if (!last && !view?.current && passes === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Earlier context was compacted to keep the chat fast — click to see the summary the model reads and every compaction so far"
        className={CHIP}
      >
        <Layers className="w-3 h-3 text-violet-500" />
        Compacted
        {passes > 1 && <span className="text-zinc-400">{passes}×</span>}
      </button>
      {open && <SummarySheet view={view} live={last} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Live per-thread Modal sandbox boot status, shown beside the compaction chip.
 * A cold boot (create sandbox + seed volume on the first command of a turn) is
 * otherwise invisible and reads as a hang. `warming` shows a spinner; `ready`
 * flashes briefly then the hook clears it; `failed` stays until the next boot.
 * Renders nothing when idle/warm.
 */
/**
 * Shown while a revert's deferred workspace restore finishes. The restore is
 * kicked off in the background by the revert and, if it hasn't finished, the
 * next turn blocks on it before producing any output — minutes on a heavy
 * thread. Without this the model just sits in "thinking", which reads as a hang.
 */
function WorkspaceRestoreWidget() {
  const restore = useWorkspaceRestoreStatus();
  if (!restore) return null;

  const captured = restore.capturedAt ? new Date(restore.capturedAt) : null;
  const when = captured && !Number.isNaN(captured.getTime())
    ? captured.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  // Count the change, not the workspace: the snapshot's file count made a
  // two-file rollback read as a full rewrite of everything in it.
  const copy = restore.copy ?? 0;
  const del = restore.delete ?? 0;
  const parts = [
    copy > 0 ? `${copy} file${copy === 1 ? '' : 's'} back` : '',
    del > 0 ? `removing ${del}` : '',
  ].filter(Boolean);

  return (
    <span
      title={
        `Putting the workspace back to the snapshot taken before the message you reverted to` +
        (when ? ` (captured ${when})` : '') +
        `. Files created after that point are deleted; files identical in both are left alone. The turn starts once this finishes.`
      }
      className={CHIP_STATIC}
    >
      <Shimmer>{parts.length > 0 ? `Rolling ${parts.join(', ')}${when ? ` to ${when}` : ''}` : 'Restoring workspace'}</Shimmer>
    </span>
  );
}

function SandboxWidget() {
  const status = useSandboxStatus();
  if (!status) return null;

  if (status.state === 'warming') {
    return (
      <span
        title="Starting this thread's sandbox (create + seed workspace). The first command runs once this is ready."
        className={CHIP_STATIC}
      >
        <Shimmer>Starting sandbox</Shimmer>
      </span>
    );
  }

  if (status.state === 'ready') {
    return (
      <span
        title={`Sandbox ready${status.elapsedMs ? ` in ${(status.elapsedMs / 1000).toFixed(1)}s` : ''}`}
        className={CHIP_STATIC}
      >
        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
        Sandbox ready
      </span>
    );
  }

  return (
    <span
      title={status.error ? `Sandbox failed to start: ${status.error}` : 'Sandbox failed to start'}
      className={CHIP_STATIC}
    >
      <XCircle className="w-3 h-3 text-red-500" />
      Sandbox error
    </span>
  );
}

interface CatalogTool {
  name: string;
  label: string;
  description: string;
}
interface CatalogCategory {
  id: string;
  label: string;
  tools: CatalogTool[];
}
interface ToolCatalogData {
  core: string[];
  categories: CatalogCategory[];
}

interface ToolPreset {
  id: string;
  name: string;
  description?: string;
  tools: string[];
  builtin?: boolean;
}
interface PresetsData {
  builtin: ToolPreset[];
  custom: ToolPreset[];
}

/**
 * Tools button + modal for picking which (non-core) tools the model may use in
 * THIS thread.  What's checked when you save IS the selection — unchecking
 * removes a tool.  Tool schemas ride in the cached prefix and are re-read on
 * every step, so shedding unused ones is a real per-request saving; it costs a
 * single cache refresh on the next turn.
 */
function ToolPicker() {
  const { currentThreadId } = useThreadState();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState<string[]>([]);

  useEffect(() => {
    if (!currentThreadId) {
      setEnabled([]);
      return;
    }
    let cancelled = false;
    api
      .get<{ selectedTools: string[] }>(`/chat/threads/${currentThreadId}/tools`)
      .then((res) => { if (!cancelled) setEnabled(res.selectedTools ?? []); })
      .catch(() => { if (!cancelled) setEnabled([]); });
    return () => { cancelled = true; };
  }, [currentThreadId]);

  const disabled = !currentThreadId;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        title={disabled ? 'Send a message first to enable tool selection' : 'Choose which tools the AI may use in this thread'}
        className={`${CHIP} disabled:text-zinc-300 disabled:hover:bg-transparent disabled:cursor-not-allowed`}
      >
        <Wrench className="w-3 h-3 text-amber-600" />
        Tools
        {enabled.length > 0 && <span className="text-zinc-400">{enabled.length}</span>}
      </button>
      {open && currentThreadId && (
        <ToolPickerModal
          threadId={currentThreadId}
          enabled={enabled}
          onClose={() => setOpen(false)}
          onSaved={(next) => setEnabled(next)}
        />
      )}
    </>
  );
}

function ToolPickerModal({
  threadId,
  enabled,
  onClose,
  onSaved,
}: {
  threadId: string;
  enabled: string[];
  onClose: () => void;
  onSaved: (next: string[]) => void;
}) {
  const [catalog, setCatalog] = useState<ToolCatalogData | null>(null);
  const [presets, setPresets] = useState<PresetsData>({ builtin: [], custom: [] });
  // `initial` is the saved state, kept only to show what this edit changes;
  // `selected` is the authoritative set the Save button sends.
  const [initial] = useState<Set<string>>(new Set(enabled));
  const [selected, setSelected] = useState<Set<string>>(new Set(enabled));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manage, setManage] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const loadPresets = useCallback(() => {
    api.get<PresetsData>('/chat/tools/presets').then(setPresets).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ToolCatalogData>('/chat/tools/catalog')
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
        const next = new Set<string>();
        for (const c of data.categories) {
          if (!c.tools.some((t) => initial.has(t.name))) next.add(c.id);
        }
        setCollapsed(next);
      })
      .catch(() => { if (!cancelled) setError('Failed to load tool catalogue'); });
    loadPresets();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleCategory = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setCategory = (catId: string, on: boolean) => {
    const cat = catalog?.categories.find((c) => c.id === catId);
    if (!cat) return;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const t of cat.tools) {
        if (on) next.add(t.name);
        else next.delete(t.name);
      }
      return next;
    });
  };

  // Presets add to the current selection rather than replacing it, so applying
  // one can't silently drop tools the user turned on by hand.
  const applyPreset = (p: ToolPreset) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const t of p.tools) next.add(t);
      return next;
    });
    // expand categories the preset touches so the additions are visible
    if (catalog) {
      setCollapsed((prev) => {
        const next = new Set(prev);
        for (const c of catalog.categories) {
          if (c.tools.some((t) => p.tools.includes(t.name))) next.delete(c.id);
        }
        return next;
      });
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const tools = [...selected];
      const res = await api.patch<{ selectedTools: string[] }>(`/chat/threads/${threadId}/tools`, { tools });
      onSaved(res.selectedTools ?? []);
      onClose();
    } catch {
      setError('Failed to save tool selection');
      setSaving(false);
    }
  };

  const saveAsPreset = async () => {
    const name = newPresetName.trim();
    const tools = [...selected];
    if (!name || tools.length === 0) return;
    setSavingPreset(true);
    try {
      await api.post('/chat/tools/presets', { name, tools });
      setNewPresetName('');
      setShowSaveInput(false);
      loadPresets();
    } catch {
      setError('Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const updatePresetToCurrent = async (p: ToolPreset) => {
    const tools = [...selected];
    try {
      await api.patch(`/chat/tools/presets/${p.id}`, { tools });
      loadPresets();
    } catch {
      setError('Failed to update preset');
    }
  };

  const deletePreset = async (p: ToolPreset) => {
    try {
      await api.delete(`/chat/tools/presets/${p.id}`);
      loadPresets();
    } catch {
      setError('Failed to delete preset');
    }
  };

  const addedCount = [...selected].filter((t) => !initial.has(t)).length;
  const removedCount = [...initial].filter((t) => !selected.has(t)).length;
  const dirty = addedCount > 0 || removedCount > 0;
  const selectedCount = selected.size;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/20" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[86vh] flex flex-col rounded-lg bg-white shadow-lg border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-zinc-500" /> Tools for this thread
            </h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-md leading-relaxed">
              Pick which tools the AI may use. Core tools (files, search, workspace) are always on.
              Unchecking a tool <span className="font-medium">removes</span> it — every enabled tool is re-sent on
              each step, so trimming unused ones shrinks each request.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Presets */}
        <div className="px-5 py-3 border-b border-zinc-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-zinc-500">Presets — click to add a task's tools</span>
            <div className="flex items-center gap-2">
              {presets.custom.length > 0 && (
                <button type="button" onClick={() => setManage((m) => !m)} className={`text-xs font-medium ${manage ? 'text-zinc-600' : 'text-zinc-400 hover:text-zinc-600'}`}>
                  {manage ? 'Done' : 'Manage'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowSaveInput((s) => !s)}
                disabled={selectedCount === 0}
                className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 disabled:opacity-40"
                title="Save the current selection as a reusable preset"
              >
                <Plus className="w-3 h-3" /> Save current
              </button>
            </div>
          </div>
          {showSaveInput && (
            <div className="flex items-center gap-2 mb-2">
              <input
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveAsPreset(); }}
                placeholder="Preset name…"
                autoFocus
                className="flex-1 px-2.5 py-1 rounded-lg border border-zinc-200 text-xs focus:outline-none focus:border-zinc-400"
              />
              <button type="button" onClick={saveAsPreset} disabled={savingPreset || !newPresetName.trim()} className="px-2.5 py-1 rounded-lg bg-accent-500 text-white text-xs font-medium hover:bg-accent-600 disabled:opacity-40">
                Save
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {presets.builtin.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                title={p.description ? `${p.description}\n\n${p.tools.length} tools` : `${p.tools.length} tools`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-zinc-200 bg-white text-xs text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 transition-colors"
              >
                {p.name}
              </button>
            ))}
            {presets.custom.map((p) => (
              <span key={p.id} className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 text-xs text-zinc-700 overflow-hidden">
                <button type="button" onClick={() => applyPreset(p)} title={`${p.tools.length} tools`} className="inline-flex items-center gap-1 px-2.5 py-1 hover:bg-zinc-100">
                  <Wrench className="w-2.5 h-2.5" />
                  {p.name}
                </button>
                {manage && (
                  <>
                    <button type="button" onClick={() => updatePresetToCurrent(p)} title="Update this preset to the current selection" className="px-1.5 py-1 border-l border-zinc-200 hover:bg-zinc-100">
                      <RotateCcw className="w-2.5 h-2.5" />
                    </button>
                    <button type="button" onClick={() => deletePreset(p)} title="Delete preset" className="px-1.5 py-1 border-l border-zinc-200 hover:bg-red-100 hover:text-red-600">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </>
                )}
              </span>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {error && <div className="mx-2 mb-2 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs">{error}</div>}
          {!catalog ? (
            <div className="flex items-center justify-center py-10 text-zinc-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            catalog.categories.map((cat) => {
              const isCollapsed = collapsed.has(cat.id);
              const onInCat = cat.tools.filter((t) => selected.has(t.name)).length;
              const addedInCat = cat.tools.filter((t) => selected.has(t.name) && !initial.has(t.name)).length;
              const removedInCat = cat.tools.filter((t) => !selected.has(t.name) && initial.has(t.name)).length;
              const allOn = onInCat === cat.tools.length;
              return (
                <div key={cat.id} className="mb-1.5 rounded-md border border-zinc-200 overflow-hidden">
                  <div className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50">
                    <button type="button" onClick={() => toggleCategory(cat.id)} className="flex items-center gap-2 text-xs font-semibold text-zinc-700 hover:text-zinc-900">
                      {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />}
                      {cat.label}
                      <span className="text-xs font-normal text-zinc-400">({cat.tools.length})</span>
                    </button>
                    <div className="flex items-center gap-2">
                      {onInCat > 0 && <span className="text-xs text-zinc-500">{onInCat} on</span>}
                      {addedInCat > 0 && <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 text-xs font-medium">+{addedInCat}</span>}
                      {removedInCat > 0 && <span className="text-xs text-red-600">−{removedInCat}</span>}
                      <button
                        type="button"
                        onClick={() => setCategory(cat.id, !allOn)}
                        className="text-xs font-medium text-zinc-400 hover:text-zinc-600"
                      >
                        {allOn ? 'Clear' : 'Select all'}
                      </button>
                    </div>
                  </div>
                  {!isCollapsed && (
                    <div className="divide-y divide-zinc-50">
                      {cat.tools.map((tool) => {
                        const checked = selected.has(tool.name);
                        const willRemove = !checked && initial.has(tool.name);
                        return (
                          <label
                            key={tool.name}
                            className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-zinc-50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(tool.name)}
                              className="mt-0.5 w-3.5 h-3.5 rounded border-zinc-300 text-zinc-600 focus:ring-zinc-400"
                            />
                            <span className="flex-1 min-w-0">
                              <span className="flex items-center gap-1.5 text-xs font-medium text-zinc-800">
                                {tool.label}
                                {willRemove && <span className="text-xs text-red-600">will be removed</span>}
                                <span className="font-mono text-xs text-zinc-300">{tool.name}</span>
                              </span>
                              {tool.description && (
                                <span className="block text-xs text-zinc-500 truncate">{tool.description}</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100">
          <span className="text-xs text-zinc-400">
            {selectedCount} selected
            {addedCount > 0 ? ` · ${addedCount} to add` : ''}
            {removedCount > 0 ? ` · ${removedCount} to remove` : ''}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-600 hover:bg-zinc-100">
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-500 text-white text-xs font-medium hover:bg-accent-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {dirty ? 'Save changes' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const PROVIDER_NAMES: Record<string, string> = { google: 'Google', openai: 'OpenAI', anthropic: 'Anthropic', 'mac-claude': 'Subscription' };

/** Popover menus: solid white, hairline border, the only place with a shadow. */
const MENU = 'rounded-lg border border-zinc-200 bg-white shadow-lg p-1 z-50';
const MENU_ITEM = 'w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded-md text-meta transition-colors';

function ModelSelector() {
  const { selectedModel, setSelectedModel } = useModelSelection();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const current = AI_MODELS.find((m) => m.id === selectedModel) ?? AI_MODELS[0];

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={CHIP}
      >
        <ProviderDot provider={current.provider} />
        {current.label}
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>

      {open && (
        <div className={`absolute bottom-full left-0 mb-1 w-52 ${MENU}`}>
          {AI_MODELS.map((m) => {
            const isActive = m.id === selectedModel;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  setSelectedModel(m.id as AiModelId);
                  setOpen(false);
                }}
                className={`${MENU_ITEM} ${isActive ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700 hover:bg-zinc-100'}`}
              >
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <ProviderDot provider={m.provider} />
                  {m.label}
                  {isActive && <Check className="w-3 h-3 text-zinc-500" />}
                </span>
                <span className="text-xs text-zinc-400 whitespace-nowrap">{PROVIDER_NAMES[m.provider] ?? m.provider}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * How hard the selected model thinks.  Remembered per model; changing it
 * mid-thread costs that thread one uncached turn, since effort is part of
 * what the providers cache.
 */
function EffortSelector() {
  const { selectedModel, selectedEffort, setSelectedEffort } = useModelSelection();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const model = AI_MODELS.find((m) => m.id === selectedModel) ?? AI_MODELS[0];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={CHIP} title="Thinking effort">
        <Brain className="w-3 h-3 text-violet-400" />
        {EFFORT_LABELS[selectedEffort]}
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>

      {open && (
        <div className={`absolute bottom-full left-0 mb-1 w-48 ${MENU}`}>
          <div className="px-2 py-1 text-xs font-medium text-zinc-500">Thinking effort · {model.label}</div>
          {model.efforts.map((level: EffortLevel) => {
            const isActive = level === selectedEffort;
            return (
              <button
                key={level}
                type="button"
                onClick={() => {
                  setSelectedEffort(level);
                  setOpen(false);
                }}
                className={`${MENU_ITEM} ${isActive ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700 hover:bg-zinc-100'}`}
              >
                <span className="flex items-center gap-1.5">
                  {EFFORT_LABELS[level]}
                  {isActive && <Check className="w-3 h-3 text-zinc-500" />}
                </span>
                {level === model.providerDefault && <span className="text-xs text-zinc-400">default</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ComposerAttachment() {
  const attachment = useThreadComposerAttachment();
  const name = attachment.name ?? 'File';
  const isImage = attachment.type === 'image';
  const file = attachment.file as File | undefined;

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!isImage || !file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [isImage, file]);

  return (
    <AttachmentPrimitive.Root className="relative group inline-flex">
      <div className="flex items-center gap-2 pl-2 pr-7 py-1.5 rounded-md bg-white border border-zinc-200 max-w-[200px]">
        {previewUrl ? (
          <img src={previewUrl} alt={name} className="w-8 h-8 rounded object-cover shrink-0" />
        ) : (
          <FileTypeIcon contentType={attachment.contentType} className="w-4 h-4 text-zinc-400 shrink-0" />
        )}
        <span className="text-xs text-zinc-700 truncate">{name}</span>
      </div>
      <AttachmentPrimitive.Remove className="absolute top-1/2 -translate-y-1/2 right-1 w-5 h-5 rounded-md text-zinc-400 flex items-center justify-center hover:text-zinc-900 hover:bg-zinc-100 transition-colors">
        <X className="w-3 h-3" />
      </AttachmentPrimitive.Remove>
    </AttachmentPrimitive.Root>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Code Block with Copy Button
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Tables are the one thing allowed to scroll sideways, inside their own box:
 * prose wraps anywhere so the chat never scrolls horizontally, but breaking a
 * number or a column name mid-word reads worse than a scroll on a wide table.
 */
function MarkdownTable({ node: _node, ...props }: any) {
  return (
    <div className="my-3 max-w-full overflow-x-auto">
      <table {...props} />
    </div>
  );
}

function MarkdownPre({ children, ...props }: any) {
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  const codeEl = React.Children.toArray(children).find(
    (child: any) => child?.type === 'code',
  ) as React.ReactElement<any> | undefined;
  const lang = codeEl?.props?.className?.match?.(/language-(\w+)/)?.[1] ?? '';

  const handleCopy = useCallback(() => {
    const text = preRef.current?.querySelector('code')?.textContent ?? '';
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  return (
    <div className="not-prose relative my-3 overflow-hidden rounded-md border border-zinc-200 bg-zinc-50">
      <div className="flex h-8 items-center justify-between border-b border-zinc-200 bg-zinc-50 pl-3 pr-1">
        <span className="font-mono text-xs text-zinc-500">{lang || 'code'}</span>
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? 'Copied' : 'Copy code'}
          aria-label={copied ? 'Copied' : 'Copy code'}
          className="flex h-6 w-6 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-200/60 hover:text-zinc-800"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <pre
        ref={preRef}
        {...props}
        className="!m-0 whitespace-pre-wrap break-words bg-zinc-50 p-3 font-mono text-xs leading-relaxed text-zinc-800 [&_code.hljs]:!bg-transparent [&_code.hljs]:!p-0 [&_code]:font-mono"
      >
        {children}
      </pre>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Drag-and-Drop Overlay
// ═══════════════════════════════════════════════════════════════════════════════

function DropZoneOverlay({ children }: { children: ReactNode }) {
  const [dragging, setDragging] = useState(false);
  const dragCountRef = useRef(0);
  let aui: ReturnType<typeof useAui> | null = null;
  try { aui = useAui(); } catch { /* not inside runtime */ }

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCountRef.current++;
    if (dragCountRef.current === 1) setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCountRef.current--;
    if (dragCountRef.current === 0) setDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    dragCountRef.current = 0;
    setDragging(false);
    if (!aui) return;
    const files = Array.from(e.dataTransfer.files);
    for (const file of files) {
      // A file the adapter refuses has been reported; the rest still attach.
      await aui.composer().addAttachment(file).catch(() => {});
    }
  }, [aui]);

  return (
    <div
      className="relative flex-1 flex flex-col min-h-0"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {dragging && (
        <div className="absolute inset-0 z-50 m-2 flex items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50 pointer-events-none">
          <div className="flex flex-col items-center gap-2">
            <Paperclip className="w-5 h-5 text-zinc-400" />
            <span className="text-meta text-zinc-600">Drop files to attach</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Image Lightbox
// ═══════════════════════════════════════════════════════════════════════════════

function ImageLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-950/90 cursor-zoom-out"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={url}
        alt=""
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-md"
        onClick={(e) => e.stopPropagation()}
      />
      <a
        href={url}
        download
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-4 right-4 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs transition-colors"
      >
        <Download className="w-3.5 h-3.5" />
        Open original
      </a>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Retry with Different Model
// ═══════════════════════════════════════════════════════════════════════════════

function RetryWithModelButton() {
  const { selectedModel, setSelectedModel } = useModelSelection();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Goes through the same stored-transcript cut as the plain re-run; retrying an
  // earlier message with another model is a branch just as much as editing is.
  const { reload } = useMessageReload();

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const otherModels = AI_MODELS.filter((m) => m.id !== selectedModel);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={MSG_ACTION}
        title="Retry with different model"
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className={`absolute left-0 top-full mt-1 w-52 ${MENU}`}>
          <div className="px-2 py-1 text-xs font-medium text-zinc-500">Retry with</div>
          {otherModels.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                setSelectedModel(m.id as AiModelId);
                setOpen(false);
                // The transport reads the model from a ref that updates on the
                // next render, so let that land before the turn is queued.
                setTimeout(() => void reload(), 50);
              }}
              className={`${MENU_ITEM} text-zinc-700 hover:bg-zinc-100`}
            >
              <span className="whitespace-nowrap">{m.label}</span>
              <span className="text-xs text-zinc-400 whitespace-nowrap">{PROVIDER_NAMES[m.provider] ?? m.provider}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
